import { useState, useEffect, useMemo } from 'react'
import type React from 'react'
import {
  LayoutDashboard,
  PlusCircle,
  Boxes,
  FileText,
  ShoppingCart,
  Scale,
  Wrench,
  QrCode,
  Smartphone,
  AlertTriangle,
  TrendingUp,
  Trash2,
  RefreshCw,
  Printer,
  CheckCircle,
  Info,
  Sun,
  Moon,
  Users,
  Sliders,
  ChevronDown,
  ChevronRight,
  Search,
  Warehouse,
  ClipboardPlus,
  ArrowDownLeft,
  ArrowUpRight,
  Archive,
  MapPin,
  FileSignature,
  DollarSign,
  Calculator,
  Settings
} from 'lucide-react'
import type { Produto, Lote, Entidade, LocalArmazenamento, Saldo, Movimentacao, Reserva, Vale, ItemOperacional, SystemParameters } from './types'
import { gerarCodigoLote, getLoteSpecsString, sanitizeInput } from './types'
import type { Usuario, PermissoesMatriz } from './App'

function FieldTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  return (
    <span 
      style={{ 
        position: 'relative', 
        display: 'inline-flex', 
        alignItems: 'center', 
        marginLeft: '6px', 
        cursor: 'pointer',
        verticalAlign: 'middle'
      }} 
      onMouseEnter={() => setShow(true)} 
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}
    >
      <Info size={13} style={{ color: 'var(--text-muted)', opacity: 0.6 }} />
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '22px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          width: '220px',
          whiteSpace: 'normal',
          zIndex: 9999,
          color: 'var(--text-high)',
          lineHeight: '1.4',
          fontWeight: 'normal',
          pointerEvents: 'none'
        }}>
          {text}
        </div>
      )}
    </span>
  )
}

interface PCAppProps {
  produtos: Produto[]
  setProdutos: React.Dispatch<React.SetStateAction<Produto[]>>
  lotes: Lote[]
  setLotes: React.Dispatch<React.SetStateAction<Lote[]>>
  locais: LocalArmazenamento[]
  setLocais: React.Dispatch<React.SetStateAction<LocalArmazenamento[]>>
  movimentacoes: Movimentacao[]
  setMovimentacoes: React.Dispatch<React.SetStateAction<Movimentacao[]>>
  reservas: Reserva[]
  setReservas: React.Dispatch<React.SetStateAction<Reserva[]>>
  vales: Vale[]
  setVales: React.Dispatch<React.SetStateAction<Vale[]>>
  entidades: Entidade[]
  setEntidades: React.Dispatch<React.SetStateAction<Entidade[]>>
  itensOperacionais: ItemOperacional[]
  setItensOperacionais: React.Dispatch<React.SetStateAction<ItemOperacional[]>>
  saldos: Saldo[]
  theme: 'light' | 'dark'
  setTheme: React.Dispatch<React.SetStateAction<'light' | 'dark'>>
  getProductPhysicalSum: (productId: string) => number
  getProductAvailableSum: (productId: string) => number
  handleAddProduct: (newProd: Omit<Produto, 'id'>) => void
  handleCreateLote: (newLote: Omit<Lote, 'id' | 'data_recebimento'>, targetLocalId: string) => Lote
  handleCreateReserva: (newRes: Omit<Reserva, 'id' | 'status'>) => void
  handleReserveBudget: (reservaId: string) => void
  handleTriggerSale: (reservaId: string) => void
  handleCancelReserva: (reservaId: string) => void
  handleAddMovimentacao: (mov: Omit<Movimentacao, 'id' | 'data_movimento'>) => void
  handleAddVale: (vale: Omit<Vale, 'id' | 'data_emprestimo'>) => void
  handleDevolverVale: (valeId: string) => void
  handlePerderVale: (valeId: string) => void
  parametros: SystemParameters
  setParametros: React.Dispatch<React.SetStateAction<SystemParameters>>
  usuarioLogado: Usuario | null
  setUsuarioLogado: React.Dispatch<React.SetStateAction<Usuario | null>>
  usuarios: Usuario[]
  setUsuarios: (newUsers: Usuario[] | ((prev: Usuario[]) => Usuario[])) => void
  permissoes: PermissoesMatriz
  setPermissoes: (newPerm: PermissoesMatriz | ((prev: PermissoesMatriz) => PermissoesMatriz)) => void
  isOnline: boolean
}

export default function PCApp({
  produtos,
  setProdutos: _setProdutos,
  lotes,
  setLotes: _setLotes,
  locais,
  setLocais,
  movimentacoes,
  setMovimentacoes: _setMovimentacoes,
  reservas,
  setReservas: _setReservas,
  vales,
  setVales: _setVales,
  entidades,
  setEntidades,
  itensOperacionais,
  setItensOperacionais,
  saldos,
  theme,
  setTheme,
  getProductPhysicalSum,
  getProductAvailableSum,
  handleAddProduct,
  handleCreateLote,
  handleCreateReserva,
  handleReserveBudget,
  handleTriggerSale,
  handleCancelReserva,
  handleAddMovimentacao,
  handleAddVale,
  handleDevolverVale,
  handlePerderVale,
  parametros,
  setParametros,
  usuarioLogado,
  setUsuarioLogado,
  usuarios,
  setUsuarios,
  permissoes,
  setPermissoes: _setPermissoes,
  isOnline: _isOnline
}: PCAppProps) {
  const [activeTab, setActiveTab] = useState<string>(() => localStorage.getItem('pc_active_tab') || 'dashboard')

  useEffect(() => {
    localStorage.setItem('pc_active_tab', activeTab)
  }, [activeTab])

  // Helper to format values as currency BRL (R$ X.XXX,XX)
  const formatCurrency = (val: number) => {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  // Helper to parse currency inputs
  const parseCurrencyInput = (valStr: string) => {
    const digits = valStr.replace(/\D/g, '')
    if (!digits) return 0
    return Number(digits) / 100
  }
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    almoxarifado: true,
    operacional: false,
    comercial: false,
    cadastros: false,
    utilitarios: false,
    admin: false
  })

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  const uniqueTons = useMemo(() => Array.from(new Set(lotes.map(l => l.tom_especifico).filter((t): t is string => !!t).map(t => sanitizeInput(t)))), [lotes])
  const uniqueCores = useMemo(() => Array.from(new Set([
    ...parametros.representacoesComerciais.map(c => sanitizeInput(c)),
    ...lotes.map(l => l.cor).filter((c): c is string => !!c).map(c => sanitizeInput(c))
  ])), [parametros.representacoesComerciais, lotes])
  const uniqueMarcas = useMemo(() => Array.from(new Set(lotes.map(l => l.marca).filter((m): m is string => !!m).map(m => sanitizeInput(m)))), [lotes])

  const uniqueSalas = useMemo(() => Array.from(new Set([
    ...locais.map(l => l.localizacao).filter(Boolean).map(s => sanitizeInput(s)),
    ...itensOperacionais.map(i => i.localizacao_sala).filter((s): s is string => !!s).map(s => sanitizeInput(s))
  ])), [locais, itensOperacionais])
  const registeredFuncionarios = useMemo(() => {
    return entidades.filter(e => e.tipo_relacao === 'funcionario' && e.ativo)
  }, [entidades])
  const uniqueSetores = useMemo(() => Array.from(new Set([
    ...parametros.setoresFabrica.map(s => sanitizeInput(s)),
    ...vales.map(v => v.setor).filter((s): s is string => !!s).map(s => sanitizeInput(s))
  ])), [parametros.setoresFabrica, vales])
  const uniqueGrupos = useMemo(() => parametros.grupos.map(g => g.nome), [parametros.grupos])
  const getSubgrupos = (grupoNome: string) => {
    const g = parametros.grupos.find(gr => gr.nome === grupoNome)
    return g ? g.subgrupos : []
  }


  // Login States
  const [loginUser, setLoginUser] = useState('')
  const [loginPass, setLoginPass] = useState('')
  const [loginErr, setLoginErr] = useState('')

  const [submitSuccess, setSubmitSuccess] = useState(false)

  // Unified Material Registration states
  const [cadNome, setCadNome] = useState('')
  const [cadGrupo, setCadGrupo] = useState(() => parametros.grupos[0]?.nome || 'MADEIRA')
  const [cadSubgrupo, setCadSubgrupo] = useState(() => parametros.grupos[0]?.subgrupos[0] || 'CHAPA')
  const [cadUnidade, setCadUnidade] = useState('un')
  const [cadTipoControle, setCadTipoControle] = useState<'simples' | 'serializado' | 'patrimonio'>('simples')
  const [cadEstMin, setCadEstMin] = useState(10)
  const [cadAgrupador, setCadAgrupador] = useState('')
  const [cadCusto, setCadCusto] = useState(0)
  const [cadVenda, setCadVenda] = useState(0)
  const [cadObs, setCadObs] = useState('')
  const [cadFilterGrupo, setCadFilterGrupo] = useState('TODOS')
  const [cadBuscaNome, setCadBuscaNome] = useState('')




  const [comprasViewMode, setComprasViewMode] = useState<'detalhada' | 'agregada'>('agregada')



  // 2. Reserva Form States
  const [numOrcamento, setNumOrcamento] = useState('')
  const [prodId, setProdId] = useState(produtos[0]?.id || '')
  const [resLoteId, setResLoteId] = useState('')
  const [qty, setQty] = useState(10)
  const [resClienteId, setResClienteId] = useState('')
  const [printingBudget, setPrintingBudget] = useState<Reserva | null>(null)

  // 3. Balanço / Ajuste Form States
  const [balancoLoteId, setBalancoLoteId] = useState(lotes[0]?.id || '')
  const [balancoLocalId, setBalancoLocalId] = useState(locais[0]?.id || '')
  const [qtyEncontrada, setQtyEncontrada] = useState(0)
  const [adjustSuccess, setAdjustSuccess] = useState(false)

  // 5. Entidade Form States
  const [entNome, setEntNome] = useState('')
  const [entTipoPessoa, setEntTipoPessoa] = useState<'PF' | 'PJ'>('PJ')
  const [entTipoRelacao, setEntTipoRelacao] = useState<'fornecedor' | 'cliente' | 'funcionario' | 'transportadora' | 'representante'>('fornecedor')
  const [entDocumento, setEntDocumento] = useState('')
  const [entNomeFantasia, setEntNomeFantasia] = useState('')
  const [entTelefone, setEntTelefone] = useState('')
  const [entEmail, setEntEmail] = useState('')
  const [entEndereco, setEntEndereco] = useState('')
  const [entCidade, setEntCidade] = useState('')
  const [entUf, setEntUf] = useState('')
  const [entCep, setEntCep] = useState('')
  const [entObservacao, setEntObservacao] = useState('')
  const [entFilter, setEntFilter] = useState<'todos' | 'fornecedor' | 'cliente' | 'funcionario' | 'transportadora' | 'representante'>('todos')
  const [entSuccess, setEntSuccess] = useState(false)
  const [entBusca, setEntBusca] = useState('')
  const [cnpjLoading, setCnpjLoading] = useState(false)

  // Local Modal Form States
  const [showLocalModal, setShowLocalModal] = useState(false)
  const [newLocalCode, setNewLocalCode] = useState('')
  const [newLocalTipo, setNewLocalTipo] = useState<'gaiola' | 'corredor' | 'prateleira' | 'patio' | 'geral'>('gaiola')
  const [newLocalSala, setNewLocalSala] = useState('')
  const [newLocalCap, setNewLocalCap] = useState('')
  const [newLocalUnitCap, setNewLocalUnitCap] = useState('un')
  const [editingLocal, setEditingLocal] = useState<LocalArmazenamento | null>(null)

  // 6. Putaway Form States
  const [putawayItemKey, setPutawayItemKey] = useState('')
  const [putawayLocalId, setPutawayLocalId] = useState('')
  const [putawayQty, setPutawayQty] = useState(0)
  const [putawaySuccess, setPutawaySuccess] = useState(false)

  // States for Saída de Estoque view
  const [manualOutboundProdName, setManualOutboundProdName] = useState('')
  const [manualOutboundLoteId, setManualOutboundLoteId] = useState('')
  const [manualOutboundQty, setManualOutboundQty] = useState(0)
  const [manualOutboundMotivo, setManualOutboundMotivo] = useState<'producao' | 'perda' | 'venda' | 'emprestimo'>('producao')
  const [manualOutboundSetor, setManualOutboundSetor] = useState('')
  const [manualOutboundSuccess, setManualOutboundSuccess] = useState(false)

  // 4. Vales Form States
  const [nomeFunc, setNomeFunc] = useState('')
  const [valeCpfCnpj, setValeCpfCnpj] = useState('')
  const [valeSetor, setValeSetor] = useState('')
  const [valeTipo, setValeTipo] = useState<'emprestimo' | 'venda'>('emprestimo')
  const [valeItemId, setValeItemId] = useState('')
  const [valeItemName, setValeItemName] = useState('')
  const [cobranca, setCobranca] = useState(150)
  const [showValePrintModal, setShowValePrintModal] = useState(false)
  const [selectedValeForPrint, setSelectedValeForPrint] = useState<Vale | null>(null)



  // 11. Aba Parâmetros States
  const [selectedParamKey, setSelectedParamKey] = useState<keyof Omit<SystemParameters, 'grupos'> | 'grupos_subgrupos'>('unidadesMedida')
  const [newParamValue, setNewParamValue] = useState('')
  const [newGroupName, setNewGroupName] = useState('')
  const [selectedGroupIdForSub, setSelectedGroupIdForSub] = useState('')
  const [newSubgroupName, setNewSubgroupName] = useState('')


  // 12. Aba Controle de Usuários States
  const [newUsrUsername, setNewUsrUsername] = useState('')
  const [newUsrPassword, setNewUsrPassword] = useState('')
  const [newUsrFullName, setNewUsrFullName] = useState('')
  const [newUsrRole, setNewUsrRole] = useState<'admin' | 'diretoria' | 'operador' | 'comprador'>('operador')
  const [usrSuccess, setUsrSuccess] = useState(false)

  // 9. Calculadora de Madeira Form States
  const [woodCalcMode, setWoodCalcMode] = useState<'volume_recibido' | 'pallet_dims' | 'pecas_pallets'>('pecas_pallets')
  const [calcWoodL, setCalcWoodL] = useState(1.86)
  const [calcWoodW, setCalcWoodW] = useState(1.36)
  const [calcWoodT, setCalcWoodT] = useState(0.005)
  const [calcTotalVolume, setCalcTotalVolume] = useState(1.0)
  const [calcPallets, setCalcPallets] = useState(1)
  const [calcStackL, setCalcStackL] = useState(180) // in cm
  const [calcStackW, setCalcStackW] = useState(100) // in cm
  const [calcStackH, setCalcStackH] = useState(120) // in cm
  const [calcTotalPecas, setCalcTotalPecas] = useState(200)

  // 10. Entrada de Insumos (Unificada) States
  const [manualInboundProdId, setManualInboundProdId] = useState(produtos[0]?.id || '')
  const [manualInboundProdName, setManualInboundProdName] = useState(() => {
    const p = produtos.find(x => x.id === (produtos[0]?.id || ''))
    return p ? p.nome : ''
  })
  const [manualInboundLoteCode, setManualInboundLoteCode] = useState('')

  const [manualInboundQty, setManualInboundQty] = useState(10)
  const [manualInboundLocalId, setManualInboundLocalId] = useState(locais[0]?.id || '')
  const [manualInboundLocalCode, setManualInboundLocalCode] = useState(() => {
    const l = locais.find(x => x.id === (locais[0]?.id || ''))
    return l ? l.codigo_local : ''
  })
  const [manualInboundFornId, setManualInboundFornId] = useState('')
  const [manualInboundFornName, setManualInboundFornName] = useState('')
  const [manualInboundSuccess, setManualInboundSuccess] = useState(false)
  const [manualInboundZpl, setManualInboundZpl] = useState('')
  const [showManualZplModal, setShowManualZplModal] = useState(false)

  // Manual Inbound cost states
  const [manualInboundCusto, setManualInboundCusto] = useState(0)
  const [manualInboundVenda, setManualInboundVenda] = useState(0)
  const [manualInboundNFNum, setManualInboundNFNum] = useState('')
  const [manualInboundNFPhoto, setManualInboundNFPhoto] = useState('')
  const [manualInboundTipoControle, setManualInboundTipoControle] = useState<'simples' | 'serializado' | 'patrimonio'>('simples')
  const [manualInboundNFDate, setManualInboundNFDate] = useState(() => {
    const d = new Date()
    const tzOffset = d.getTimezoneOffset() * 60000
    const localISODate = new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
    return localISODate
  })

  useEffect(() => {
    setManualInboundVenda(manualInboundCusto > 0 ? Number((manualInboundCusto * 1.3).toFixed(2)) : 0)
  }, [manualInboundCusto])

  useEffect(() => {
    const prod = produtos.find(p => p.id === manualInboundProdId)
    if (prod) {
      setManualInboundTipoControle(prod.tipo_controle)
    }
  }, [manualInboundProdId, produtos])
  // const [lastImportedLotes, setLastImportedLotes] = useState<Lote[]>([])








  useEffect(() => {
    setCadVenda(cadCusto > 0 ? Number((cadCusto * 1.3).toFixed(2)) : 0)
  }, [cadCusto])

  // Sync manual inbound dropdowns
  useEffect(() => {
    if (produtos.length > 0) {
      if (!manualInboundProdId) setManualInboundProdId(produtos[0].id)
    }
  }, [produtos, manualInboundProdId])

  useEffect(() => {
    if (locais.length > 0) {
      if (!manualInboundLocalId) setManualInboundLocalId(locais[0].id)
    }
  }, [locais, manualInboundLocalId])

  useEffect(() => {
    const suppliers = entidades.filter(e => e.tipo_relacao === 'fornecedor')
    if (suppliers.length > 0) {
      if (!manualInboundFornId) setManualInboundFornId(suppliers[0].id)
    }
  }, [entidades, manualInboundFornId])

  // Auto-generate suggested batch code in Manual Inbound Form
  useEffect(() => {
    const prod = produtos.find(p => p.id === manualInboundProdId)
    if (prod) {
      const generated = gerarCodigoLote(
        prod.grupo,
        prod.subgrupo
      )
      setManualInboundLoteCode(generated)
    }
  }, [manualInboundProdId, produtos])



  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!loginUser.trim() || !loginPass.trim()) return

    const match = usuarios.find(u => u.username.toUpperCase() === loginUser.trim().toUpperCase())
    if (match && match.senha === loginPass) {
      setUsuarioLogado(match)
      setLoginUser('')
      setLoginPass('')
      setLoginErr('')
    } else {
      setLoginErr('Usuário ou senha incorretos.')
    }
  }

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUsrUsername.trim() || !newUsrPassword.trim() || !newUsrFullName.trim()) {
      alert('Preencha todos os campos.')
      return
    }

    const upperUsername = newUsrUsername.trim().toUpperCase()
    if (usuarios.some(u => u.username.toUpperCase() === upperUsername)) {
      alert('Este usuário já está cadastrado.')
      return
    }

    const newUser: Usuario = {
      username: upperUsername,
      senha: newUsrPassword.trim(),
      nome: newUsrFullName.trim(),
      nivel: newUsrRole
    }

    setUsuarios(prev => [...prev, newUser])
    setNewUsrUsername('')
    setNewUsrPassword('')
    setNewUsrFullName('')
    setNewUsrRole('operador')
    setUsrSuccess(true)
    setTimeout(() => setUsrSuccess(false), 3000)
  }

  const handleDeleteUser = (username: string) => {
    if (username.toUpperCase() === 'ADMIN') {
      alert('Não é possível remover o Administrador principal.')
      return
    }
    if (usuarioLogado && usuarioLogado.username.toUpperCase() === username.toUpperCase()) {
      alert('Você não pode se auto-excluir.')
      return
    }

    if (confirm(`Remover usuário "${username}"?`)) {
      setUsuarios(prev => prev.filter(u => u.username.toUpperCase() !== username.toUpperCase()))
    }
  }

  // Sync state drop-downs on product list updates
  useEffect(() => {
    if (produtos.length > 0) {
      if (!prodId) setProdId(produtos[0].id)
    }
  }, [produtos, prodId])

  useEffect(() => {
    if (itensOperacionais.length > 0) {
      if (!valeItemId) setValeItemId(itensOperacionais[0].id)
    }
  }, [itensOperacionais, valeItemId])

  useEffect(() => {
    if (lotes.length > 0) {
      if (!balancoLoteId) setBalancoLoteId(lotes[0].id)
    }
  }, [lotes])

  useEffect(() => {
    if (locais.length > 0) {
      if (!balancoLocalId) setBalancoLocalId(locais[0].id)
    }
  }, [locais])

  // Sync Lote options in Reservas when selected product changes
  useEffect(() => {
    const productLotes = lotes.filter(l => l.produto_id === prodId)
    if (productLotes.length > 0) {
      setResLoteId(productLotes[0].id)
    } else {
      setResLoteId('')
    }
  }, [prodId, lotes])

  // 1. Dashboard View
  const renderPCDashboard = () => {
    const productsBelowMin = produtos.filter(p => {
      const av = getProductAvailableSum(p.id)
      return av < p.estoque_minimo
    })

    const totalReservations = reservas.filter(r => r.status === 'reservado').length

    // Compute stock financial valuation
    let totalCustoEstoque = 0
    let totalVendaEstoque = 0

    produtos.forEach(p => {
      const fis = getProductPhysicalSum(p.id)
      totalCustoEstoque += fis * (p.preco_compra || 0)
      totalVendaEstoque += fis * (p.preco_venda || 0)
    })

    const margemEstoque = totalVendaEstoque > 0 ? ((totalVendaEstoque - totalCustoEstoque) / totalVendaEstoque) * 100 : 0

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
        {/* Top summary row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(244, 63, 94, 0.15)', padding: '12px', borderRadius: '12px', color: 'var(--color-danger)' }}>
              <AlertTriangle size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Estoque Crítico</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-high)' }}>{productsBelowMin.length} itens</div>
            </div>
          </div>
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '12px', borderRadius: '12px', color: 'var(--color-success)' }}>
              <FileText size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Reservas Ativas</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-high)' }}>{totalReservations} pedidos</div>
            </div>
          </div>
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'var(--badge-bg-primary)', padding: '12px', borderRadius: '12px', color: 'var(--color-primary)' }}>
              <ShoppingCart size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Custo Inventário</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-high)' }}>R$ {totalCustoEstoque.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(168, 85, 247, 0.15)', padding: '12px', borderRadius: '12px', color: 'var(--color-secondary)' }}>
              <TrendingUp size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Venda Potencial</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-high)' }}>R$ {totalVendaEstoque.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '12px', borderRadius: '12px', color: 'var(--color-success)' }}>
              <RefreshCw size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Margem Estimada</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-high)' }}>{margemEstoque.toFixed(1)}%</div>
            </div>
          </div>
        </div>

        {/* Central tables & Alerts */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
          <div className="glass-panel" style={{ padding: '20px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-high)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Boxes size={20} /> Posição Consolidada de Saldo
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '10px' }}>Produto</th>
                  <th style={{ padding: '10px' }}>Categoria</th>
                  <th style={{ padding: '10px' }}>Saldo Físico</th>
                  <th style={{ padding: '10px' }}>Reservado</th>
                  <th style={{ padding: '10px' }}>Disponível</th>
                  <th style={{ padding: '10px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {produtos.map(p => {
                  const fis = getProductPhysicalSum(p.id)
                  const res = reservas
                    .filter(r => r.produto_id === p.id && r.status === 'reservado')
                    .reduce((sum, r) => sum + r.quantidade_reservada, 0)
                  const disp = fis - res
                  const isBelow = disp < p.estoque_minimo

                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.03)' }}>
                      <td style={{ padding: '12px 10px', color: 'var(--text-high)', fontWeight: '500' }}>{p.nome}</td>
                      <td style={{ padding: '12px 10px' }}><span className="badge badge-primary">{p.grupo} - {p.subgrupo}</span></td>
                      <td style={{ padding: '12px 10px' }}>{fis.toFixed(2)} {p.unidade_medida_padrao}</td>
                      <td style={{ padding: '12px 10px', color: res > 0 ? 'var(--color-warning)' : 'inherit' }}>{res.toFixed(2)}</td>
                      <td style={{ padding: '12px 10px', fontWeight: 'bold', color: isBelow ? 'var(--color-danger)' : 'var(--color-success)' }}>
                        {disp.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        {isBelow ? (
                          <span className="badge badge-danger">Repor</span>
                        ) : (
                          <span className="badge badge-success">OK</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Atividade de Galpão</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '350px', overflowY: 'auto' }}>
              {movimentacoes.slice().reverse().map(m => {
                const p = produtos.find(prod => prod.id === m.produto_id)
                const l = lotes.find(lot => lot.id === m.lote_id)
                const isIncoming = m.tipo_movimento === 'entrada' || (m.tipo_movimento === 'ajuste' && m.quantidade > 0)
                
                return (
                  <div key={m.id} style={{ borderLeft: `3px solid ${isIncoming ? 'var(--color-success)' : 'var(--color-danger)'}`, paddingLeft: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-high)' }}>
                        {m.tipo_movimento.toUpperCase()} ({m.motivo})
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                        {new Date(m.data_movimento).toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-medium)' }}>
                      {p?.nome} ({l?.codigo_lote})
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: isIncoming ? 'var(--color-success)' : 'var(--color-danger)' }}>
                      {isIncoming ? '+' : '-'}{Math.abs(m.quantidade)} {p?.unidade_medida_padrao}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }


  // Unified Material Registration
  const renderPCCadastroMaterial = () => {
    const handleMaterialSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!cadNome) return

      const finalBarras = `BAR-${cadGrupo.substring(0,3).toUpperCase()}-${Date.now()}`

      handleAddProduct({
        nome: cadNome,
        codigo_barras: finalBarras,
        tipo_controle: cadTipoControle,
        grupo: cadGrupo.toUpperCase(),
        subgrupo: cadSubgrupo.toUpperCase(),
        unidade_medida_padrao: cadUnidade,
        estoque_minimo: Number(cadEstMin),
        agrupador_comercial: cadAgrupador || 'Geral',
        preco_compra: Number(cadCusto),
        preco_venda: Number(cadVenda),
        observacoes: cadObs
      })

      setSubmitSuccess(true)
      setCadNome('')
      setCadAgrupador('')
      setCadCusto(0)
      setCadVenda(0)
      setCadObs('')
      setTimeout(() => setSubmitSuccess(false), 3000)
    }

    const filteredProds = produtos.filter(p => {
      const matchGrupo = cadFilterGrupo === 'TODOS' || p.grupo.toUpperCase() === cadFilterGrupo.toUpperCase()
      const matchSearch = !cadBuscaNome.trim() || 
        p.nome.toLowerCase().includes(cadBuscaNome.toLowerCase()) || 
        p.codigo_barras.includes(cadBuscaNome)
      return matchGrupo && matchSearch
    })

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Cadastrar Novo Material</h3>

          {submitSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)', fontSize: '13px' }}>
              Material cadastrado com sucesso!
            </div>
          )}

          <form onSubmit={handleMaterialSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* Secção 1: Identificação */}
            <div className="form-section">
              <span className="form-section-title">1. Identificação Básica</span>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Nome / Descrição *</label>
                <input className="form-input" type="text" placeholder="Ex: Eucalipto 1.86x1.36 ou Suede Bege" value={cadNome} onChange={e => setCadNome(sanitizeInput(e.target.value))} required />
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Grupo *</label>
                  <select className="form-select" value={cadGrupo} onChange={e => {
                    const grp = e.target.value
                    setCadGrupo(grp)
                    const subs = getSubgrupos(grp)
                    if (subs.length > 0) setCadSubgrupo(subs[0])
                  }} required>
                    {parametros.grupos.map(g => (
                      <option key={g.id} value={g.nome}>{g.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Subgrupo *</label>
                  <select className="form-select" value={cadSubgrupo} onChange={e => setCadSubgrupo(e.target.value)} required>
                    {getSubgrupos(cadGrupo).map(sg => (
                      <option key={sg} value={sg}>{sg}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Secção 2: Parametrização e Controle */}
            <div className="form-section">
              <span className="form-section-title">2. Controle & Regras WMS</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Unidade Padrão *</label>
                  <select className="form-select" value={cadUnidade} onChange={e => setCadUnidade(e.target.value)} required>
                    {parametros.unidadesMedida.map(u => (
                      <option key={u} value={u}>{u.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Tipo de Controle *</label>
                  <select className="form-select" value={cadTipoControle} onChange={e => setCadTipoControle(e.target.value as any)} required>
                    <option value="simples">SIMPLES (SALDO GERAL)</option>
                    <option value="serializado">SERIALIZADO (PEÇA A PEÇA)</option>
                    <option value="patrimonio">PATRIMÔNIO (OPERACIONAL)</option>
                  </select>
                </div>
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Estoque Mínimo de Alerta *</label>
                  <input className="form-input" type="number" step="any" value={cadEstMin} onChange={e => setCadEstMin(Number(e.target.value))} required />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Agrupador Comercial</label>
                  <input className="form-input" type="text" placeholder="Ex: Chapa 1.86x1.36" value={cadAgrupador} onChange={e => setCadAgrupador(sanitizeInput(e.target.value))} list="agrupadores-datalist" />
                </div>
              </div>
            </div>

            {/* Secção 3: Valores Financeiros */}
            <div className="form-section">
              <span className="form-section-title">3. Valores Comerciais & Observações</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Custo de Compra (R$)</label>
                  <input className="form-input" type="text" value={formatCurrency(cadCusto)} onChange={e => setCadCusto(parseCurrencyInput(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Preço Venda (+30% sugerido R$)</label>
                  <input className="form-input" type="text" value={formatCurrency(cadVenda)} onChange={e => setCadVenda(parseCurrencyInput(e.target.value))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Observações / Especificações Livres</label>
                <textarea className="form-input" rows={2} placeholder="Ex: Grampo 14/40 em caixas com 10.000 un." value={cadObs} onChange={e => setCadObs(sanitizeInput(e.target.value))} />
              </div>
            </div>

            <div className="form-row-actions">
              <button className="btn-primary" type="submit" style={{ padding: '12px 24px' }}>
                Registrar Material
              </button>
            </div>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', height: 'fit-content' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Materiais Cadastrados</h3>
            <span style={{ fontSize: '12px', background: 'rgba(99, 102, 241, 0.15)', color: 'var(--color-primary)', padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
              {filteredProds.length} itens
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <select className="form-select" value={cadFilterGrupo} onChange={e => setCadFilterGrupo(e.target.value)}>
                <option value="TODOS">TODOS OS GRUPOS</option>
                {parametros.grupos.map(g => (
                  <option key={g.id} value={g.nome}>{g.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <input className="form-input" type="text" placeholder="Buscar por nome ou barras..." value={cadBuscaNome} onChange={e => setCadBuscaNome(e.target.value)} />
            </div>
          </div>

          <div style={{ overflowX: 'auto', maxHeight: '520px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '10px 6px' }}>Nome</th>
                  <th style={{ padding: '10px 6px' }}>Grupo / Subgrupo</th>
                  <th style={{ padding: '10px 6px', textAlign: 'center' }}>UM</th>
                  <th style={{ padding: '10px 6px', textAlign: 'right' }}>Est. Mín.</th>
                  <th style={{ padding: '10px 6px', textAlign: 'right' }}>Venda</th>
                </tr>
              </thead>
              <tbody>
                {filteredProds.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum material cadastrado nesta seleção.</td>
                  </tr>
                ) : (
                  filteredProds.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-high)' }}>
                      <td style={{ padding: '10px 6px', fontWeight: '500' }}>
                        <div>{p.nome}</div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{p.codigo_barras}</span>
                      </td>
                      <td style={{ padding: '10px 6px' }}>
                        <span style={{ fontSize: '11px', background: 'var(--bg-inset)', padding: '2px 6px', borderRadius: '4px', marginRight: '4px', border: '1px solid var(--border-color)' }}>{p.grupo}</span>
                        <span style={{ color: 'var(--text-medium)' }}>{p.subgrupo}</span>
                      </td>
                      <td style={{ padding: '10px 6px', textAlign: 'center', fontWeight: 'bold' }}>{p.unidade_medida_padrao.toUpperCase()}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right' }}>{p.estoque_minimo}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', color: 'var(--color-primary)', fontWeight: 'bold' }}>
                        {p.preco_venda ? `R$ ${p.preco_venda.toFixed(2)}` : 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }




  const renderPCPutaway = () => {
    const stagingSaldos = saldos.filter(s => s.local_id === 'local-doca-recebimento' && s.quantidade_fisica > 0)
    
    const handleSelectForPutaway = (s: Saldo) => {
      setPutawayItemKey(`${s.produto_id}_${s.lote_id}`)
      setPutawayQty(s.quantidade_fisica)
      const defaultDest = locais.find(l => l.id !== 'local-doca-recebimento')
      setPutawayLocalId(defaultDest?.id || '')
    }

    const handlePutawaySubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!putawayItemKey || !putawayLocalId || putawayQty <= 0) return

      const [prodId, loteId] = putawayItemKey.split('_')
      
      const matchSaldo = saldos.find(s => s.produto_id === prodId && s.lote_id === loteId && s.local_id === 'local-doca-recebimento')
      if (!matchSaldo) {
        alert('Lote não encontrado na doca.')
        return
      }

      if (putawayQty > matchSaldo.quantidade_fisica) {
        alert('Quantidade de guarda não pode exceder o saldo atual na doca.')
        return
      }

      handleAddMovimentacao({
        produto_id: prodId,
        lote_id: loteId,
        local_origem_id: 'local-doca-recebimento',
        local_destino_id: putawayLocalId,
        tipo_movimento: 'transferencia',
        motivo: 'compra',
        quantidade: putawayQty,
        usuario: 'Guarda (Putaway) Administrativo'
      })

      setPutawaySuccess(true)
      setPutawayItemKey('')
      setPutawayQty(0)
      setTimeout(() => setPutawaySuccess(false), 3000)
    }

    const selectedSaldo = stagingSaldos.find(s => `${s.produto_id}_${s.lote_id}` === putawayItemKey)
    const selectedProd = selectedSaldo ? produtos.find(p => p.id === selectedSaldo.produto_id) : null
    const selectedLote = selectedSaldo ? lotes.find(l => l.id === selectedSaldo.lote_id) : null

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '20px', height: 'fit-content' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Efetuar Guarda (Putaway)</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Transfira os materiais parados na doca temporária de recebimento para as posições definitivas de estoque.
          </p>

          {putawaySuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.3)', marginBottom: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={16} /> Transferência de guarda executada com sucesso!
            </div>
          )}

          <form onSubmit={handlePutawaySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Item Selecionado</label>
              <input 
                className="form-input" 
                type="text" 
                readOnly 
                value={selectedProd ? `${selectedProd.nome} (${selectedLote?.codigo_lote})` : 'Nenhum lote selecionado'} 
                style={{ opacity: 0.7 }}
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Quantidade a Guardar ({selectedProd?.unidade_medida_padrao.toUpperCase() || ''})</label>
              <input 
                className="form-input" 
                type="number" 
                step="any"
                value={putawayQty || ''} 
                onChange={e => setPutawayQty(Number(e.target.value))} 
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Local de Destino</label>
              <select className="form-select" value={putawayLocalId} onChange={e => setPutawayLocalId(e.target.value)} required>
                <option value="">Selecione a posição de estoque</option>
                {locais.filter(l => l.id !== 'local-doca-recebimento').map(l => (
                  <option key={l.id} value={l.id}>{l.codigo_local} ({l.tipo.toUpperCase()})</option>
                ))}
              </select>
            </div>

            <button className="btn-primary" type="submit" disabled={!putawayItemKey} style={{ justifyContent: 'center', padding: '10px' }}>
              Confirmar Guarda
            </button>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Fila de Entrada (Doca Temporária)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="wms-table">
              <thead>
                <tr>
                  <th style={{ padding: '10px' }}>Material</th>
                  <th style={{ padding: '10px' }}>Lote / Identificador</th>
                  <th style={{ padding: '10px' }}>Saldo na Doca</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {stagingSaldos.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                      Nenhum material pendente de guarda na doca.
                    </td>
                  </tr>
                ) : (
                  stagingSaldos.map(s => {
                    const p = produtos.find(prod => prod.id === s.produto_id)
                    const l = lotes.find(lot => lot.id === s.lote_id)
                    return (
                      <tr key={`${s.produto_id}_${s.lote_id}`} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '10px' }}>{p?.nome}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{l?.codigo_lote}</span>
                        </td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                          {s.quantidade_fisica.toFixed(2)} {p?.unidade_medida_padrao.toUpperCase()}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right' }}>
                          <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => handleSelectForPutaway(s)}>
                            Selecionar
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const renderPCLocais = () => {
    const locaisFiltrados = locais

    const handleEditLocal = (loc: LocalArmazenamento) => {
      setEditingLocal(loc)
      setNewLocalCode(loc.codigo_local)
      setNewLocalTipo(loc.tipo)
      setNewLocalSala(loc.localizacao)
      setNewLocalCap(loc.capacidade_maxima ? String(loc.capacidade_maxima) : '')
      setNewLocalUnitCap(loc.unidade_capacidade || 'un')
      setShowLocalModal(true)
    }

    const handleDeleteLocal = (loc: LocalArmazenamento) => {
      if (confirm(`Deseja realmente excluir o endereço de estoque "${loc.codigo_local}"?`)) {
        setLocais(prev => prev.filter(x => x.id !== loc.id))
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Endereços de Armazenagem</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
              Locais físicos identificados para todas as posições de estoque do galpão.
            </p>
          </div>
          <button className="btn-secondary" onClick={() => {
            setEditingLocal(null)
            setNewLocalCode('')
            setNewLocalTipo('gaiola')
            setNewLocalSala('')
            setNewLocalCap('')
            setNewLocalUnitCap('un')
            setShowLocalModal(true)
          }}><PlusCircle size={16} /> Novo Local</button>
        </div>

        {showLocalModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="glass-panel" style={{ padding: '24px', width: '400px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ margin: 0, color: 'var(--text-high)' }}>
                {editingLocal ? 'Editar Endereço' : 'Cadastrar Endereço'}
              </h3>
              <form onSubmit={(e) => {
                e.preventDefault()
                if (!newLocalCode || !newLocalSala) {
                  alert('Preencha os campos obrigatórios.')
                  return
                }
                if (editingLocal) {
                  setLocais(prev => prev.map(l => l.id === editingLocal.id ? {
                    ...l,
                    codigo_local: newLocalCode.toUpperCase().trim(),
                    tipo: newLocalTipo,
                    localizacao: newLocalSala,
                    capacidade_maxima: newLocalCap ? Number(newLocalCap) : undefined,
                    unidade_capacidade: newLocalUnitCap ? newLocalUnitCap as any : undefined
                  } : l))
                } else {
                  setLocais(prev => [...prev, {
                    id: `local-${Date.now()}`,
                    codigo_local: newLocalCode.toUpperCase().trim(),
                    tipo: newLocalTipo,
                    localizacao: newLocalSala,
                    capacidade_maxima: newLocalCap ? Number(newLocalCap) : undefined,
                    unidade_capacidade: newLocalUnitCap ? newLocalUnitCap as any : undefined
                  }])
                }
                setEditingLocal(null)
                setShowLocalModal(false)
              }} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                
                {/* Secção 1: Identificação */}
                <div className="form-section">
                  <span className="form-section-title">1. Identificação do Endereço</span>
                  <div className="form-grid-2">
                    <div>
                      <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Código do Local *</label>
                      <input 
                        className="form-input" 
                        type="text" 
                        placeholder="Ex: CORREDOR-M2" 
                        value={newLocalCode} 
                        onChange={e => setNewLocalCode(sanitizeInput(e.target.value))} 
                        required 
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Tipo de Local</label>
                      <select className="form-select" value={newLocalTipo} onChange={e => setNewLocalTipo(e.target.value as any)}>
                        <option value="gaiola">Gaiola</option>
                        <option value="corredor">Corredor</option>
                        <option value="prateleira">Prateleira</option>
                        <option value="patio">Pátio</option>
                        <option value="geral">Geral</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Secção 2: Localização física */}
                <div className="form-section">
                  <span className="form-section-title">2. Localização Física</span>
                  <div>
                    <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Localização / Área / Sala *</label>
                    <input 
                      className="form-input" 
                      type="text" 
                      placeholder="Ex: Ala Lateral - Madeiras, Galpão Geral" 
                      value={newLocalSala} 
                      onChange={e => setNewLocalSala(sanitizeInput(e.target.value))} 
                      required 
                    />
                  </div>
                </div>

                {/* Secção 3: Capacidade */}
                <div className="form-section">
                  <span className="form-section-title">3. Capacidade de Armazenagem</span>
                  <div className="form-grid-2">
                    <div>
                      <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Capacidade (Opcional)</label>
                      <input 
                        className="form-input" 
                        type="number" 
                        placeholder="Ex: 500" 
                        value={newLocalCap} 
                        onChange={e => setNewLocalCap(e.target.value)} 
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Unidade de Capacidade</label>
                      <select className="form-select" value={newLocalUnitCap} onChange={e => setNewLocalUnitCap(e.target.value)}>
                        {parametros.unidadesMedida.map(u => (
                          <option key={u} value={u}>{u.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px', borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}>
                  <button type="button" className="btn-secondary" onClick={() => {
                    setEditingLocal(null)
                    setShowLocalModal(false)
                  }}>Cancelar</button>
                  <button type="submit" className="btn-primary">Salvar</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
          {locaisFiltrados.map(g => {
            const itemsInLocal = saldos.filter(s => s.local_id === g.id && s.quantidade_fisica > 0)
            
            const occupied = itemsInLocal.reduce((sum, item) => sum + item.quantidade_fisica, 0)
            const capMax = g.capacidade_maxima || 0
            const capUnit = g.unidade_capacidade || ''
            const pct = capMax > 0 ? (occupied / capMax) * 100 : 0
            
            let color = '#10b981'
            if (pct >= 70 && pct < 90) color = '#f59e0b'
            if (pct >= 90) color = '#ef4444'

            return (
              <div key={g.id} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                  <div>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-high)' }}>{g.codigo_local}</span>
                    <span className="badge badge-primary" style={{ marginLeft: '8px', fontSize: '10px', padding: '2px 6px' }}>{g.tipo}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{g.localizacao}</span>
                    <button
                      onClick={() => handleEditLocal(g)}
                      className="btn-secondary"
                      style={{ padding: '2px 6px', fontSize: '10px', height: '22px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                      title="Editar Endereço"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteLocal(g)}
                      disabled={itemsInLocal.length > 0}
                      className="btn-secondary"
                      style={{
                        padding: '2px 6px',
                        fontSize: '10px',
                        height: '22px',
                        border: 'none',
                        background: 'transparent',
                        color: itemsInLocal.length > 0 ? 'var(--text-muted)' : 'var(--color-danger)',
                        cursor: itemsInLocal.length > 0 ? 'not-allowed' : 'pointer'
                      }}
                      title={itemsInLocal.length > 0 ? "Endereço possui estoque ativo" : "Excluir Endereço"}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Capacity Gauges Section */}
                {capMax > 0 && (
                  <div style={{ background: 'var(--bg-inset)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '500' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Ocupação Física:</span>
                      <span style={{ color: 'var(--text-high)', fontWeight: 'bold' }}>
                        {occupied.toFixed(2)} / {capMax} {capUnit} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, transition: 'width 0.3s ease' }}></div>
                    </div>
                  </div>
                )}

                {/* Items detail list inside card */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '4px' }}>
                    Saldos Armazenados
                  </div>
                  {itemsInLocal.length === 0 ? (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
                      Nenhum saldo neste local.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                      {itemsInLocal.map(s => {
                        const prod = produtos.find(p => p.id === s.produto_id)
                        const lote = lotes.find(l => l.id === s.lote_id)
                        return (
                          <div key={`${s.produto_id}_${s.lote_id}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', background: 'rgba(255,255,255,0.01)', padding: '6px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.03)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: '500', color: 'var(--text-high)' }}>{prod?.nome}</span>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lote: {lote?.codigo_lote}</span>
                            </div>
                            <strong style={{ alignSelf: 'center', color: 'var(--color-primary)' }}>
                              {s.quantidade_fisica.toFixed(2)} {prod?.unidade_medida_padrao.toUpperCase()}
                            </strong>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }




  // 4. Budget Reservations View (Orçamentos e Pedidos)
  const renderPCReservas = () => {
    const handleCreateRes = (e: React.FormEvent) => {
      e.preventDefault()
      if (!resLoteId) return
      
      handleCreateReserva({
        numero_orcamento: numOrcamento || `ORC-${Math.floor(Math.random() * 90000) + 10000}`,
        produto_id: prodId,
        lote_id: resLoteId,
        quantidade_reservada: qty,
        validade: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        cliente_id: resClienteId || undefined
      })
      setNumOrcamento('')
      setResClienteId('')
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '20px', height: 'fit-content' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Gerar Orçamento</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Crie uma cotação de insumo para o cliente. Não bloqueia estoque até ser promovida a pedido reservado.
          </p>
          <form onSubmit={handleCreateRes} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* Secção 1: Cabeçalho */}
            <div className="form-section">
              <span className="form-section-title">1. Dados do Orçamento</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Cliente (Parceiro)</label>
                  <select className="form-select" value={resClienteId} onChange={e => setResClienteId(e.target.value)} required>
                    <option value="">-- Selecione o Cliente --</option>
                    {entidades.filter(ent => ent.tipo_relacao === 'cliente').map(ent => (
                      <option key={ent.id} value={ent.id}>{ent.nome} ({ent.documento})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Identificação do Orçamento</label>
                  <input className="form-input" type="text" placeholder="Ex: ORC-55829" value={numOrcamento} onChange={e => setNumOrcamento(e.target.value)} required />
                </div>
              </div>
            </div>

            {/* Secção 2: Insumo & Lote */}
            <div className="form-section">
              <span className="form-section-title">2. Produto & Reserva de Estoque</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Produto</label>
                  <select className="form-select" value={prodId} onChange={e => setProdId(e.target.value)}>
                    {produtos.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Lote / Tom Específico</label>
                  <select className="form-select" value={resLoteId} onChange={e => setResLoteId(e.target.value)} required>
                    {lotes.filter(l => l.produto_id === prodId).map(l => (
                      <option key={l.id} value={l.id}>{l.codigo_lote} ({l.tom_especifico})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Quantidade Solicitada</label>
                <input className="form-input" type="number" step="any" value={qty} onChange={e => setQty(Number(e.target.value))} />
              </div>
            </div>

            <div className="form-row-actions">
              <button className="btn-primary" type="submit" style={{ padding: '12px 24px' }}>
                Salvar Orçamento (Rascunho)
              </button>
            </div>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={20} /> Orçamentos e Pedidos Ativos
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Orçamentos emitidos (em rascunho) ou promovidos a Pedido Reservado (bloqueando estoque para separação).
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '10px' }}>Documento</th>
                <th style={{ padding: '10px' }}>Produto / Lote</th>
                <th style={{ padding: '10px' }}>Quantidade</th>
                <th style={{ padding: '10px' }}>Valor Total (Venda)</th>
                <th style={{ padding: '10px' }}>Status</th>
                <th style={{ padding: '10px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {reservas.map(r => {
                const p = produtos.find(prod => prod.id === r.produto_id)
                const l = lotes.find(lot => lot.id === r.lote_id)
                const cli = entidades.find(ent => ent.id === r.cliente_id)
                const totalValue = r.quantidade_reservada * (p?.preco_venda || 0)
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '12px 10px', color: 'var(--text-high)', fontWeight: 'bold' }}>
                      <div>{r.numero_orcamento}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                        Cliente: {cli ? cli.nome : 'Geral'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ color: 'var(--text-high)' }}>{p?.nome}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lote: {l?.codigo_lote} ({l?.tom_especifico}){l && getLoteSpecsString(l) ? ` - ${getLoteSpecsString(l)}` : ''}</div>
                    </td>
                    <td style={{ padding: '12px 10px', fontWeight: '600' }}>{r.quantidade_reservada} {p?.unidade_medida_padrao}</td>
                    <td style={{ padding: '12px 10px', fontWeight: '600', color: 'var(--color-success)' }}>
                      R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      <span className={`badge ${
                        r.status === 'rascunho' ? 'badge-primary' : 
                        r.status === 'reservado' ? 'badge-warning' : 
                        r.status === 'vendido' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {r.status === 'rascunho' ? 'Orçamento' : 
                         r.status === 'reservado' ? 'Reservado/Separado' : 
                         r.status === 'vendido' ? 'Vendido' : 'Cancelado'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 10px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button
                        className="btn-secondary"
                        style={{ padding: '6px 10px', fontSize: '12px', display: 'inline-flex', gap: '4px', alignItems: 'center' }}
                        onClick={() => setPrintingBudget(r)}
                        title="Imprimir/Visualizar Orçamento"
                      >
                        <Printer size={13} />
                        Imprimir
                      </button>

                      {r.status === 'rascunho' && (
                        <>
                          <button
                            className="btn-primary"
                            style={{ padding: '6px 10px', fontSize: '12px', display: 'inline-flex', gap: '4px', alignItems: 'center', background: 'linear-gradient(135deg, var(--color-warning), var(--color-secondary))' }}
                            onClick={() => handleReserveBudget(r.id)}
                            title="Aprovar e Reservar Estoque"
                          >
                            <Boxes size={13} />
                            Reservar
                          </button>
                          <button
                            className="btn-secondary"
                            style={{ padding: '6px 10px', fontSize: '12px', display: 'inline-flex', gap: '4px', alignItems: 'center', color: 'var(--color-danger)' }}
                            onClick={() => handleCancelReserva(r.id)}
                            title="Cancelar Orçamento"
                          >
                            Cancelar
                          </button>
                        </>
                      )}

                      {r.status === 'reservado' && (
                        <>
                          <button
                            className="btn-primary"
                            style={{ padding: '6px 10px', fontSize: '12px', display: 'inline-flex', gap: '4px', alignItems: 'center' }}
                            onClick={() => handleTriggerSale(r.id)}
                            title="Faturar e dar saída física"
                          >
                            <CheckCircle size={13} />
                            Vendido
                          </button>
                          <button
                            className="btn-secondary"
                            style={{ padding: '6px 10px', fontSize: '12px', display: 'inline-flex', gap: '4px', alignItems: 'center', color: 'var(--color-danger)' }}
                            onClick={() => handleCancelReserva(r.id)}
                            title="Liberar Reserva"
                          >
                            Liberar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Orçamento Print Modal Overlay */}
        {printingBudget && (() => {
          const prod = produtos.find(p => p.id === printingBudget.produto_id)
          const lote = lotes.find(l => l.id === printingBudget.lote_id)
          const cli = entidades.find(c => c.id === printingBudget.cliente_id)
          
          return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px'
            }}>
              <div className="glass-panel" style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '30px',
                width: '100%',
                maxWidth: '550px',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Impressão de Orçamento</h3>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>THOTH WMS - DOCUMENTO DE RESERVA</span>
                  </div>
                  <span className={`badge ${
                    printingBudget.status === 'rascunho' ? 'badge-primary' :
                    printingBudget.status === 'reservado' ? 'badge-warning' :
                    printingBudget.status === 'vendido' ? 'badge-success' : 'badge-danger'
                  }`}>
                    {printingBudget.status === 'rascunho' ? 'Orçamento' :
                     printingBudget.status === 'reservado' ? 'Reservado' :
                     printingBudget.status === 'vendido' ? 'Vendido' : 'Cancelado'}
                  </span>
                </div>

                {/* Document Sheet Layout */}
                <div style={{
                  background: '#ffffff',
                  color: '#1e293b',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontFamily: 'Courier New, monospace',
                  fontSize: '13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px dashed #cbd5e1', paddingBottom: '10px' }}>
                    PRINCE CAMA BOX LTDA
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>CLIENTE:</span>
                    <span style={{ fontWeight: 'bold' }}>{cli ? cli.nome.toUpperCase() : 'GERAL'}</span>
                  </div>
                  {cli && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>CNPJ/CPF:</span>
                      <span>{cli.documento}</span>
                    </div>
                  )}
                  <div style={{ borderBottom: '1px dashed #cbd5e1', margin: '4px 0' }}></div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Nº DO ORÇAMENTO:</span>
                    <span style={{ fontWeight: 'bold' }}>{printingBudget.numero_orcamento}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>DATA EMISSÃO:</span>
                    <span>{new Date().toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>VALIDADE:</span>
                    <span>{new Date(printingBudget.validade).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div style={{ borderBottom: '1px dashed #cbd5e1', margin: '8px 0' }}></div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>PRODUTO/INSUMO:</span>
                    <div style={{ paddingLeft: '10px', marginTop: '4px' }}>{prod?.nome}</div>
                  </div>

                  {prod?.grupo === 'TECIDO' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>TIPO DE TECIDO:</span>
                      <span>{prod.subgrupo}</span>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>LOTE:</span>
                    <span>{lote?.codigo_lote}</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>TOM ESPECÍFICO:</span>
                    <span>{lote?.tom_especifico || 'PADRAO'}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>PREÇO UNITÁRIO:</span>
                    <span>R$ {(prod?.preco_venda || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>QTD SOLICITADA:</span>
                    <span>{printingBudget.quantidade_reservada.toFixed(2)} {prod?.unidade_medida_padrao}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 'bold', borderTop: '1px dashed #cbd5e1', paddingTop: '10px', marginTop: '8px' }}>
                    <span>VALOR TOTAL ESTIMADO:</span>
                    <span>R$ {(printingBudget.quantidade_reservada * (prod?.preco_venda || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>

                  <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '20px', marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{ borderTop: '1px solid #94a3b8', width: '200px', textAlign: 'center' }}></div>
                    <span style={{ fontSize: '10px', color: '#64748b' }}>Assinatura Autorização Almoxarifado</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                  <button className="btn-secondary" onClick={() => setPrintingBudget(null)}>Fechar</button>
                  <button className="btn-primary" onClick={() => {
                    alert('Orçamento enviado para a fila de impressão comercial!')
                    setPrintingBudget(null)
                  }}>
                    <Printer size={16} /> Confirmar Impressão
                  </button>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    )
  }

  // 5. Purchases View
  const renderPCCompras = () => {
    // 1. Detailed View: items individually below minimum
    const productsBelow = produtos.filter(p => {
      const av = getProductAvailableSum(p.id)
      return av < p.estoque_minimo
    })

    // 2. Aggregated View: group by category and agrupador_comercial
    interface GroupedProduct {
      key: string
      categoria: string
      agrupador_comercial: string
      produtos: Produto[]
      estoque_fisico: number
      estoque_reservado: number
      estoque_disponivel: number
      estoque_minimo: number
      unidade: string
    }

    const groups: { [key: string]: GroupedProduct } = {}
    produtos.forEach(p => {
      const key = `${p.grupo}::${p.agrupador_comercial || 'Geral'}`
      const fis = getProductPhysicalSum(p.id)
      const res = reservas
        .filter(r => r.produto_id === p.id && r.status === 'reservado')
        .reduce((sum, r) => sum + r.quantidade_reservada, 0)
      const av = fis - res
      
      if (!groups[key]) {
        groups[key] = {
          key,
          categoria: p.grupo,
          agrupador_comercial: p.agrupador_comercial || 'Geral',
          produtos: [],
          estoque_fisico: 0,
          estoque_reservado: 0,
          estoque_disponivel: 0,
          estoque_minimo: 0,
          unidade: p.unidade_medida_padrao
        }
      }
      groups[key].produtos.push(p)
      groups[key].estoque_fisico += fis
      groups[key].estoque_reservado += res
      groups[key].estoque_disponivel += av
      groups[key].estoque_minimo += p.estoque_minimo
    })

    // Only alert on groups where the overall available is less than minimum
    const groupsBelow = Object.values(groups).filter(g => g.estoque_disponivel < g.estoque_minimo)

    return (
      <div className="glass-panel" style={{ padding: '24px', flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Sugestão de Reposição de Estoque</h3>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
              Consolidação de níveis mínimos de estoque para compras de suprimentos e materiais de fabricação.
            </p>
          </div>
          <button className="btn-primary" onClick={() => alert('Ordem de Compra exportada com sucesso!')}>
            <Printer size={16} /> Imprimir Ordem de Compra
          </button>
        </div>

        {/* View Switcher Toggle */}
        <div style={{ display: 'flex', gap: '8px', margin: '16px 0', background: 'var(--bg-inset)', padding: '4px', borderRadius: '8px', width: 'fit-content', border: '1px solid var(--border-color)' }}>
          <button 
            type="button"
            className="btn-tab" 
            style={{ 
              padding: '6px 12px', 
              fontSize: '12px', 
              borderRadius: '6px', 
              background: comprasViewMode === 'agregada' ? 'var(--color-primary)' : 'transparent',
              color: comprasViewMode === 'agregada' ? '#fff' : 'var(--text-medium)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }} 
            onClick={() => setComprasViewMode('agregada')}
          >
            Visão do Comprador (Agregada)
          </button>
          <button 
            type="button"
            className="btn-tab" 
            style={{ 
              padding: '6px 12px', 
              fontSize: '12px', 
              borderRadius: '6px', 
              background: comprasViewMode === 'detalhada' ? 'var(--color-primary)' : 'transparent',
              color: comprasViewMode === 'detalhada' ? '#fff' : 'var(--text-medium)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }} 
            onClick={() => setComprasViewMode('detalhada')}
          >
            Visão Detalhada (Estoque)
          </button>
        </div>

        {/* Dynamic Table Render */}
        {((comprasViewMode === 'detalhada' && productsBelow.length === 0) || 
          (comprasViewMode === 'agregada' && groupsBelow.length === 0)) ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--color-success)' }}>
            <CheckCircle size={48} style={{ marginBottom: '12px' }} />
            <h4>Todo o estoque está em nível seguro! Nenhuma compra urgente necessária.</h4>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '10px' }}>
                  {comprasViewMode === 'agregada' ? 'Agrupador Comercial' : 'Insumo (SKU)'}
                </th>
                <th style={{ padding: '10px' }}>Estoque Atual</th>
                <th style={{ padding: '10px' }}>Mínimo Crítico</th>
                <th style={{ padding: '10px' }}>Consumo Médio / Mês</th>
                <th style={{ padding: '10px' }}>Sugestão de Compra</th>
                <th style={{ padding: '10px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {comprasViewMode === 'detalhada' ? (
                productsBelow.map(p => {
                  const fis = getProductPhysicalSum(p.id)
                  const res = reservas
                    .filter(r => r.produto_id === p.id && r.status === 'reservado')
                    .reduce((sum, r) => sum + r.quantidade_reservada, 0)
                  const av = fis - res
                  
                  const monthlyUsage = p.grupo === 'TECIDO' ? 200 : p.grupo === 'MADEIRA' ? 1.5 : 8
                  const buySuggestion = (p.estoque_minimo * 2) - av
                  
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 10px', color: 'var(--text-high)', fontWeight: 'bold' }}>
                        <div>{p.nome}</div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                          Agrupador: {p.agrupador_comercial}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px', color: 'var(--color-danger)' }}>{av.toFixed(2)} {p.unidade_medida_padrao}</td>
                      <td style={{ padding: '12px 10px' }}>{p.estoque_minimo} {p.unidade_medida_padrao}</td>
                      <td style={{ padding: '12px 10px' }}>{monthlyUsage.toFixed(2)} / mês</td>
                      <td style={{ padding: '12px 10px', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                        {buySuggestion > 0 ? buySuggestion.toFixed(2) : 0} {p.unidade_medida_padrao}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => {
                          const lotName = prompt('Nome/Código do novo Lote:', `LOTE-${p.grupo.substring(0,3).toUpperCase()}-${Date.now().toString().slice(-4)}`)
                          if (!lotName) return

                          const suppliers = entidades.filter(e => e.tipo_relacao === 'fornecedor')
                          let supplierMsg = 'Selecione o Fornecedor digitando o número correspondente:\n'
                          suppliers.forEach((s, idx) => {
                            supplierMsg += `${idx + 1} - ${s.nome}\n`
                          })
                          const supplierIdxStr = prompt(supplierMsg, '1')
                          let selectedFornId: string | undefined = undefined
                          if (supplierIdxStr) {
                            const idx = parseInt(supplierIdxStr) - 1
                            if (idx >= 0 && idx < suppliers.length) {
                              selectedFornId = suppliers[idx].id
                            }
                          }

                          handleCreateLote({
                            codigo_lote: lotName,
                            produto_id: p.id,
                            tom_especifico: 'Lote de Reposição',
                            quantidade_nota: buySuggestion,
                            custo_unitario_compra: p.preco_compra || (p.grupo === 'TECIDO' ? 8.5 : 1200),
                            fornecedor_id: selectedFornId
                          }, p.grupo === 'MADEIRA' ? 'local-corredor-m1' : 'local-gaiola-t1')
                        }}>Simular Compra</button>
                      </td>
                    </tr>
                  )
                })
              ) : (
                groupsBelow.map(g => {
                  const monthlyUsage = g.produtos.reduce((sum, p) => sum + (p.grupo === 'TECIDO' ? 200 : p.grupo === 'MADEIRA' ? 1.5 : 8), 0)
                  const buySuggestion = (g.estoque_minimo * 2) - g.estoque_disponivel
                  
                  return (
                    <tr key={g.key} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 10px', color: 'var(--text-high)', fontWeight: 'bold' }}>
                        <div>{g.agrupador_comercial}</div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                          Categoria: {g.categoria.toUpperCase()} · SKUs no grupo: {g.produtos.length}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px', color: 'var(--color-danger)' }}>{g.estoque_disponivel.toFixed(2)} {g.unidade}</td>
                      <td style={{ padding: '12px 10px' }}>{g.estoque_minimo.toFixed(2)} {g.unidade}</td>
                      <td style={{ padding: '12px 10px' }}>{monthlyUsage.toFixed(2)} / mês</td>
                      <td style={{ padding: '12px 10px', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                        {buySuggestion > 0 ? buySuggestion.toFixed(2) : 0} {g.unidade}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => {
                          // Select which SKU in the group to buy
                          let chosenProduct = g.produtos[0]
                          if (g.produtos.length > 1) {
                            let msg = 'Escolha o SKU específico para receber esta compra (digite o número):\n'
                            g.produtos.forEach((prod, i) => {
                              msg += `${i + 1} - ${prod.nome}\n`
                            })
                            const choice = prompt(msg, '1')
                            if (choice) {
                              const idx = parseInt(choice) - 1
                              if (idx >= 0 && idx < g.produtos.length) {
                                chosenProduct = g.produtos[idx]
                              }
                            } else {
                              return // user cancelled
                            }
                          }

                          const lotName = prompt('Nome/Código do novo Lote:', `LOTE-${chosenProduct.grupo.substring(0,3).toUpperCase()}-${Date.now().toString().slice(-4)}`)
                          if (!lotName) return

                          const suppliers = entidades.filter(e => e.tipo_relacao === 'fornecedor')
                          let supplierMsg = 'Selecione o Fornecedor digitando o número correspondente:\n'
                          suppliers.forEach((s, idx) => {
                            supplierMsg += `${idx + 1} - ${s.nome}\n`
                          })
                          const supplierIdxStr = prompt(supplierMsg, '1')
                          let selectedFornId: string | undefined = undefined
                          if (supplierIdxStr) {
                            const idx = parseInt(supplierIdxStr) - 1
                            if (idx >= 0 && idx < suppliers.length) {
                              selectedFornId = suppliers[idx].id
                            }
                          }

                          // Let's specify wood type if madeira or tone details for tessuto if relevant
                          let lotWoodType: any = undefined
                          let lotTom = 'Lote de Reposição'

                          if (chosenProduct.grupo === 'MADEIRA') {
                            const woodTypeStr = prompt('Escolha o tipo de madeira (1 - pinus, 2 - eucalipto):', '2')
                            lotWoodType = woodTypeStr === '1' ? 'pinus' : 'eucalipto'
                          } else if (chosenProduct.grupo === 'TECIDO') {
                            const toneInput = prompt('Especifique a tonalidade/tom do lote:', 'Tom Padrão')
                            if (toneInput) lotTom = toneInput
                          }

                          handleCreateLote({
                            codigo_lote: lotName,
                            produto_id: chosenProduct.id,
                            tom_especifico: lotTom,
                            tipo_madeira: lotWoodType,
                            quantidade_nota: buySuggestion,
                            custo_unitario_compra: chosenProduct.preco_compra || (chosenProduct.grupo === 'TECIDO' ? 8.5 : 1200),
                            fornecedor_id: selectedFornId
                          }, chosenProduct.grupo === 'MADEIRA' ? 'local-corredor-m1' : 'local-gaiola-t1')
                        }}>Simular Compra</button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    )
  }

  // 6. Inventory Adjustments (Balanço) View
  const renderPCBalanco = () => {
    const selectedLote = lotes.find(l => l.id === balancoLoteId)
    const selectedProd = selectedLote ? produtos.find(p => p.id === selectedLote.produto_id) : null
    
    const matchSaldo = saldos.find(s => s.lote_id === balancoLoteId && s.local_id === balancoLocalId)
    const qtyEsperada = matchSaldo ? matchSaldo.quantidade_fisica : 0

    const handleAdjust = (e: React.FormEvent) => {
      e.preventDefault()
      if (!balancoLoteId || !balancoLocalId) return

      const diff = qtyEncontrada - qtyEsperada
      if (diff === 0) return

      handleAddMovimentacao({
        produto_id: selectedLote!.produto_id,
        lote_id: balancoLoteId,
        local_origem_id: diff < 0 ? balancoLocalId : undefined,
        local_destino_id: diff > 0 ? balancoLocalId : undefined,
        tipo_movimento: 'ajuste',
        motivo: 'balanco',
        quantidade: diff,
        usuario: 'Gestor de Balanço'
      })

      setAdjustSuccess(true)
      setTimeout(() => setAdjustSuccess(false), 3000)
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '20px', height: 'fit-content' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Lançar Inventário (Ajuste)</h3>
          
          {adjustSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>
              Ajuste físico lançado e saldo recalculado!
            </div>
          )}

          <form onSubmit={handleAdjust} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Lote / Item do Inventário</label>
              <select className="form-select" value={balancoLoteId} onChange={e => setBalancoLoteId(e.target.value)}>
                {lotes.map(l => {
                  const p = produtos.find(prod => prod.id === l.produto_id)
                  return (
                    <option key={l.id} value={l.id}>{l.codigo_lote} - {p?.nome}</option>
                  )
                })}
              </select>
            </div>
            
            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Local de Armazenagem</label>
              <select className="form-select" value={balancoLocalId} onChange={e => setBalancoLocalId(e.target.value)}>
                {locais.map(g => (
                  <option key={g.id} value={g.id}>{g.codigo_local} ({g.tipo})</option>
                ))}
              </select>
            </div>

            <div style={{ background: 'var(--bg-inset)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Esperado no Sistema:</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-high)' }}>
                {qtyEsperada.toFixed(2)} {selectedProd?.unidade_medida_padrao}
              </div>
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Contagem Física Real</label>
              <input className="form-input" type="number" step="any" value={qtyEncontrada} onChange={e => setQtyEncontrada(Number(e.target.value))} />
            </div>

            <div style={{ fontSize: '12px', color: qtyEncontrada - qtyEsperada < 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
              Diferença a ajustar: {(qtyEncontrada - qtyEsperada).toFixed(2)} {selectedProd?.unidade_medida_padrao}
            </div>

            <button className="btn-primary" type="submit" style={{ marginTop: '10px' }}>Confirmar Ajuste</button>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Histórico de Ajustes e Balanços</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Lançamentos imutáveis de correções de inventário.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '10px' }}>Data</th>
                <th style={{ padding: '10px' }}>Insumo</th>
                <th style={{ padding: '10px' }}>Local</th>
                <th style={{ padding: '10px' }}>Diferença</th>
                <th style={{ padding: '10px' }}>Operador</th>
              </tr>
            </thead>
            <tbody>
              {movimentacoes.filter(m => m.tipo_movimento === 'ajuste').map(m => {
                const p = produtos.find(prod => prod.id === m.produto_id)
                const l = lotes.find(lot => lot.id === m.lote_id)
                const g = locais.find(g => g.id === (m.local_destino_id || m.local_origem_id))
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '12px 10px' }}>{new Date(m.data_movimento).toLocaleDateString()}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ color: 'var(--text-high)' }}>{p?.nome}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lote: {l?.codigo_lote}</div>
                    </td>
                    <td style={{ padding: '12px 10px', fontWeight: 'bold' }}>{g?.codigo_local}</td>
                    <td style={{ padding: '12px 10px', fontWeight: 'bold', color: m.quantidade < 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                      {m.quantidade > 0 ? '+' : ''}{m.quantidade.toFixed(2)} {p?.unidade_medida_padrao}
                    </td>
                    <td style={{ padding: '12px 10px' }}>{m.usuario}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // 7. Tool Loan Management (Vales)
  const renderPCFerramentas = () => {
    const handleCreateValeForm = (e: React.FormEvent) => {
      e.preventDefault()
      if (!valeItemName.trim() || !nomeFunc.trim()) return

      const cleanItemName = sanitizeInput(valeItemName)
      let finalItemId = ''
      let selectedItem = itensOperacionais.find(i => i.nome.toUpperCase() === cleanItemName)
      
      if (selectedItem) {
        finalItemId = selectedItem.id
      } else {
        // Criar novo item de almoxarifado on-the-fly
        const newId = `item-op-${Date.now()}`
        const newItem: ItemOperacional = {
          id: newId,
          nome: cleanItemName,
          categoria: 'ferramenta',
          quantidade_total: 10,
          quantidade_disponivel: 10,
          estoque_minimo: 2,
          localizacao_sala: 'SALA DE FERRAMENTAS / ALMOXARIFADO 2',
          status: 'ativo',
          custo_aquisicao: cobranca,
          observacoes_modelo: 'CADASTRADO AUTOMATICAMENTE VIA EMISSAO DE VALE'
        }
        setItensOperacionais(prev => [...prev, newItem])
        finalItemId = newId
        selectedItem = newItem
      }

      if (selectedItem.quantidade_disponivel <= 0) {
        alert('Este item não possui quantidade disponível em estoque para empréstimo ou venda.')
        return
      }

      const cleanNomeFunc = sanitizeInput(nomeFunc)
      const emp = registeredFuncionarios.find(x => x.nome.toUpperCase() === cleanNomeFunc.toUpperCase())
      if (!emp) {
        alert(`Erro: Colaborador "${cleanNomeFunc}" não cadastrado. Cadastre o funcionário na aba "Entidades" antes de emitir um vale.`)
        return
      }

      handleAddVale({
        item_operacional_id: finalItemId,
        funcionario_nome: emp.nome,
        cpf_cnpj: sanitizeInput(valeCpfCnpj) || '000.000.000-00',
        setor: sanitizeInput(valeSetor) || 'OPERACIONAL GERAL',
        tipo: valeTipo,
        status: valeTipo === 'venda' ? 'vendido' : 'emprestado',
        valor_cobranca: cobranca,
        observacoes: valeTipo === 'venda' ? 'Item vendido' : 'Retirado sob garantia de devolução'
      })

      setNomeFunc('')
      setValeCpfCnpj('')
      setValeSetor('')
      setValeItemName('')
      setValeItemId('')
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '20px', height: 'fit-content' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Lançar Novo Vale (Empréstimo/Venda)</h3>
          <form onSubmit={handleCreateValeForm} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
            
            {/* Secção 1: Operação e Item */}
            <div className="form-section">
              <span className="form-section-title">1. Operação & Insumo</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Tipo de Operação</label>
                  <select className="form-select" value={valeTipo} onChange={e => {
                    const t = e.target.value as 'emprestimo' | 'venda'
                    setValeTipo(t)
                    if (t === 'venda') setCobranca(0)
                  }}>
                    <option value="emprestimo">Empréstimo (Vale)</option>
                    <option value="venda">Venda / Saída Definitiva</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Item (Almoxarifado)</label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Selecione ou digite um item" 
                    list="itens-operacionais-datalist" 
                    value={valeItemName} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setValeItemName(val)
                      const match = itensOperacionais.find(i => i.nome.toUpperCase() === val.toUpperCase())
                      if (match) {
                        setValeItemId(match.id)
                        if (match.custo_aquisicao) setCobranca(match.custo_aquisicao)
                      } else {
                        setValeItemId('')
                      }
                    }} 
                    required
                  />
                </div>
              </div>
            </div>

            {/* Secção 2: Responsável */}
            <div className="form-section">
              <span className="form-section-title">2. Identificação do Colaborador</span>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Nome Completo do Colaborador / Cliente</label>
                <input 
                  className="form-input" 
                  type="text" 
                  placeholder="Selecione um colaborador cadastrado" 
                  list="funcionarios-datalist"
                  value={nomeFunc} 
                  onChange={e => {
                    const val = sanitizeInput(e.target.value)
                    setNomeFunc(val)
                    const match = registeredFuncionarios.find(f => f.nome.toUpperCase() === val.toUpperCase())
                    if (match) {
                      setValeCpfCnpj(match.documento)
                    }
                  }} 
                  required 
                />
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>CPF / CNPJ</label>
                  <input className="form-input" type="text" placeholder="Ex: 000.000.000-00" value={valeCpfCnpj} onChange={e => setValeCpfCnpj(sanitizeInput(e.target.value))} required />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>Setor</label>
                  <select 
                    className="form-select" 
                    value={valeSetor} 
                    onChange={e => setValeSetor(e.target.value)} 
                    required 
                  >
                    <option value="">Selecione um Setor</option>
                    {parametros.setoresFabrica.map(s => (
                      <option key={s} value={s}>
                        {s.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Secção 3: Financeiro */}
            <div className="form-section">
              <span className="form-section-title">3. Financeiro & Garantias</span>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '4px' }}>
                  {valeTipo === 'venda' ? 'Preço de Venda R$' : 'Valor de Garantia / Reposição R$'}
                </label>
                <input className="form-input" type="number" step="any" value={cobranca} onChange={e => setCobranca(Number(e.target.value))} />
              </div>
            </div>

            <div className="form-row-actions" style={{ marginTop: '10px', paddingTop: '10px' }}>
              <button className="btn-primary" type="submit" style={{ padding: '10px 20px', width: '100%', justifyContent: 'center' }}>
                Lançar Movimentação
              </button>
            </div>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={20} /> Vales Emitidos e Vendas do Almoxarifado
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Controle de ferramentas sob empréstimo, itens devolvidos, perdidos ou vendidos.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '10px' }}>Pessoa / CPF / Setor</th>
                <th style={{ padding: '10px' }}>Item Requisitado</th>
                <th style={{ padding: '10px' }}>Tipo / Data</th>
                <th style={{ padding: '10px' }}>Valor</th>
                <th style={{ padding: '10px' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {vales.map(v => {
                const item = itensOperacionais.find(i => i.id === v.item_operacional_id)
                return (
                  <tr key={v.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '12px 10px', color: 'var(--text-high)' }}>
                      <div style={{ fontWeight: 'bold' }}>{v.funcionario_nome}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>CPF: {v.cpf_cnpj} | Setor: {v.setor}</div>
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ fontWeight: '500' }}>{item?.nome || 'Item Removido'}</div>
                      {item?.codigo_patrimonio && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pat: {item.codigo_patrimonio}</div>}
                    </td>
                    <td style={{ padding: '12px 10px', fontSize: '12px' }}>
                      <span className={`badge ${v.tipo === 'venda' ? 'badge-success' : 'badge-primary'}`} style={{ marginRight: '6px', fontSize: '10px' }}>
                        {v.tipo === 'venda' ? 'Venda' : 'Empréstimo'}
                      </span>
                      <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>{new Date(v.data_emprestimo).toLocaleDateString()}</div>
                    </td>
                    <td style={{ padding: '12px 10px', fontWeight: 'bold' }}>R$ {v.valor_cobranca.toFixed(2)}</td>
                    <td style={{ padding: '12px 10px' }}>
                      <span className={`badge ${
                        v.status === 'emprestado' ? 'badge-warning' : 
                        v.status === 'devolvido' ? 'badge-success' : 
                        v.status === 'vendido' ? 'badge-success' : 'badge-danger'
                      }`} style={{ textTransform: 'capitalize' }}>
                        {v.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => {
                          setSelectedValeForPrint(v)
                          setShowValePrintModal(true)
                        }}>
                          Recibo
                        </button>
                        {v.status === 'emprestado' && (
                          <>
                            <button className="btn-success" style={{ padding: '4px 8px', fontSize: '11px' }} onClick={() => handleDevolverVale(v.id)}>
                              Devolver
                            </button>
                            <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} onClick={() => handlePerderVale(v.id)}>
                              Perda
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }



  // 9. Calculadora de Madeira
  const renderPCCalculadora = () => {
    const L = calcWoodL
    const W = calcWoodW
    const T = calcWoodT
    const vUnit = L * W * T
    const aUnit = L * W

    let totalPecas = 0
    let pecasPorPallet = 0
    let totalVolume = 0
    let volumePorPallet = 0

    if (woodCalcMode === 'volume_recibido') {
      totalVolume = calcTotalVolume
      totalPecas = vUnit > 0 ? Math.round(calcTotalVolume / vUnit) : 0
      pecasPorPallet = calcPallets > 0 ? Math.round(totalPecas / calcPallets) : 0
      volumePorPallet = calcPallets > 0 ? totalVolume / calcPallets : 0
    } else if (woodCalcMode === 'pallet_dims') {
      const stackL_m = calcStackL / 100
      const stackW_m = calcStackW / 100
      const stackH_m = calcStackH / 100

      const nL = L > 0 ? Math.max(1, Math.floor(stackL_m / L)) : 1
      const nW = W > 0 ? Math.max(1, Math.floor(stackW_m / W)) : 1
      const nT = T > 0 ? Math.max(1, Math.floor(stackH_m / T)) : 1

      pecasPorPallet = nL * nW * nT
      totalPecas = pecasPorPallet * calcPallets
      totalVolume = totalPecas * vUnit
      volumePorPallet = pecasPorPallet * vUnit
    } else if (woodCalcMode === 'pecas_pallets') {
      totalPecas = calcTotalPecas
      pecasPorPallet = calcPallets > 0 ? Math.round(calcTotalPecas / calcPallets) : 0
      totalVolume = totalPecas * vUnit
      volumePorPallet = pecasPorPallet * vUnit
    }

    const selectPresetWood = (productId: string) => {
      const prod = produtos.find(p => p.id === productId)
      if (prod) {
        const match = prod.nome.match(/(\d+)\s*[xX]\s*(\d+)\s*[xX]\s*([\d.,]+)\s*cm/)
        if (match) {
          setCalcWoodL(Number(match[1]) / 100)
          setCalcWoodW(Number(match[2]) / 100)
          setCalcWoodT(parseFloat(match[3].replace(',', '.')) / 100)
        } else {
          setCalcWoodL(1.86)
          setCalcWoodW(1.36)
          setCalcWoodT(0.005)
        }
      }
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', flex: 1 }}>
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Calculadora de Cubagem e Pallet de Madeira</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
            Selecione o modo de cálculo que deseja utilizar para fazer a recepção física ou cubicagem de madeira no galpão.
          </p>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button
              type="button"
              className="btn-secondary"
              style={{
                flex: 1,
                padding: '10px 6px',
                fontSize: '12px',
                justifyContent: 'center',
                background: woodCalcMode === 'volume_recibido' ? 'var(--badge-bg-primary)' : 'transparent',
                borderColor: woodCalcMode === 'volume_recibido' ? 'var(--color-primary)' : 'var(--border-color)',
                color: woodCalcMode === 'volume_recibido' ? 'var(--text-high)' : 'var(--text-medium)'
              }}
              onClick={() => setWoodCalcMode('volume_recibido')}
            >
              1. Por Volume Recebido (m³)
            </button>
            <button
              type="button"
              className="btn-secondary"
              style={{
                flex: 1,
                padding: '10px 6px',
                fontSize: '12px',
                justifyContent: 'center',
                background: woodCalcMode === 'pallet_dims' ? 'var(--badge-bg-primary)' : 'transparent',
                borderColor: woodCalcMode === 'pallet_dims' ? 'var(--color-primary)' : 'var(--border-color)',
                color: woodCalcMode === 'pallet_dims' ? 'var(--text-high)' : 'var(--text-medium)'
              }}
              onClick={() => setWoodCalcMode('pallet_dims')}
            >
              2. Por Empilhamento
            </button>
            <button
              type="button"
              className="btn-secondary"
              style={{
                flex: 1,
                padding: '10px 6px',
                fontSize: '12px',
                justifyContent: 'center',
                background: woodCalcMode === 'pecas_pallets' ? 'var(--badge-bg-primary)' : 'transparent',
                borderColor: woodCalcMode === 'pecas_pallets' ? 'var(--color-primary)' : 'var(--border-color)',
                color: woodCalcMode === 'pecas_pallets' ? 'var(--text-high)' : 'var(--text-medium)'
              }}
              onClick={() => setWoodCalcMode('pecas_pallets')}
            >
              3. Por Qtd de Peças
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Selecione um Produto de Madeira Cadastrado (Preset)</label>
              <select className="form-select" onChange={e => selectPresetWood(e.target.value)} defaultValue="">
                <option value="" disabled>-- Selecione um preset (opcional) --</option>
                {produtos.filter(p => p.grupo === 'MADEIRA').map(p => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </select>
            </div>

            <div style={{ background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--color-primary)' }}>Dimensões da Madeira (Metros)</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Comprimento (m)</label>
                  <input className="form-input" type="number" step="any" value={calcWoodL} onChange={e => setCalcWoodL(Number(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Largura (m)</label>
                  <input className="form-input" type="number" step="any" value={calcWoodW} onChange={e => setCalcWoodW(Number(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Espessura (m)</label>
                  <input className="form-input" type="number" step="any" value={calcWoodT} onChange={e => setCalcWoodT(Number(e.target.value))} />
                </div>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Volume Unitário Calculado: <strong style={{ color: 'var(--text-high)' }}>{vUnit.toFixed(6)} m³</strong>
              </div>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Quantidade de Pallets / Pilhas</label>
              <input className="form-input" type="number" min={1} value={calcPallets} onChange={e => setCalcPallets(Math.max(1, Number(e.target.value)))} />
            </div>

            {woodCalcMode === 'volume_recibido' && (
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Volume Total Recebido (m³)</label>
                <input className="form-input" type="number" step="any" min={0} value={calcTotalVolume} onChange={e => setCalcTotalVolume(Number(e.target.value))} />
              </div>
            )}

            {woodCalcMode === 'pallet_dims' && (
              <div style={{ background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--color-primary)' }}>Dimensões da Pilha/Pallet (Centímetros)</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Comprimento Pilha (cm)</label>
                    <input className="form-input" type="number" value={calcStackL} onChange={e => setCalcStackL(Number(e.target.value))} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Largura Pilha (cm)</label>
                    <input className="form-input" type="number" value={calcStackW} onChange={e => setCalcStackW(Number(e.target.value))} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Altura Pilha (cm)</label>
                    <input className="form-input" type="number" value={calcStackH} onChange={e => setCalcStackH(Number(e.target.value))} />
                  </div>
                </div>
              </div>
            )}

            {woodCalcMode === 'pecas_pallets' && (
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Quantidade de Peças Totais</label>
                <input className="form-input" type="number" min={1} value={calcTotalPecas} onChange={e => setCalcTotalPecas(Math.max(1, Number(e.target.value)))} />
              </div>
            )}
          </div>
        </div>

        <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Resultados do Cálculo</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, justifyContent: 'center' }}>
            <div style={{ background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>MADEIRA POR PALLET</span>
                <strong style={{ fontSize: '24px', color: 'var(--color-primary)' }}>{pecasPorPallet} peças</strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>TOTAL DE PEÇAS</span>
                <strong style={{ fontSize: '24px', color: 'var(--text-high)' }}>{totalPecas} peças</strong>
              </div>
            </div>

            <div style={{ background: 'var(--bg-inset)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>VOLUME DO PALLET (m³)</span>
                <strong style={{ fontSize: '20px', color: 'var(--color-success)' }}>{volumePorPallet.toFixed(4)} m³</strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>VOLUME TOTAL (m³)</span>
                <strong style={{ fontSize: '20px', color: 'var(--text-high)' }}>{totalVolume.toFixed(4)} m³</strong>
              </div>
            </div>

            <div style={{ background: 'var(--bg-inset)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '12px', color: 'var(--text-muted)' }}>
              <div>Área por peça: <strong>{aUnit.toFixed(4)} m²</strong></div>
              <div>Área total estimada: <strong>{(totalPecas * aUnit).toFixed(2)} m²</strong></div>
              <div style={{ marginTop: '6px', fontSize: '11px', fontStyle: 'italic' }}>
                *Lógica de cálculo executada em tempo real com base no método selecionado. O operador pode copiar esses valores para o recebimento de romaneio ou coletor mobile.
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }



  // 8. Entities Registry View (Parceiros: Clientes e Fornecedores)
  const renderPCEntidades = () => {
    const formatTelefone = (value: string) => {
      const clean = value.replace(/\D/g, '')
      if (clean.length === 0) return ''
      if (clean.length <= 2) return `(${clean}`
      if (clean.length <= 6) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`
      if (clean.length <= 10) return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7, 11)}`
    }

    const formatDocumento = (value: string, tipoPessoa: 'PF' | 'PJ') => {
      const clean = value.replace(/\D/g, '')
      if (tipoPessoa === 'PF') {
        if (clean.length === 0) return ''
        if (clean.length <= 3) return clean
        if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`
        if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`
        return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`
      } else {
        if (clean.length === 0) return ''
        if (clean.length <= 2) return clean
        if (clean.length <= 5) return `${clean.slice(0, 2)}.${clean.slice(2)}`
        if (clean.length <= 8) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5)}`
        if (clean.length <= 12) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8)}`
        return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12, 14)}`
      }
    }

    const formatCEP = (value: string) => {
      const clean = value.replace(/\D/g, '')
      if (clean.length === 0) return ''
      if (clean.length <= 5) return clean
      return `${clean.slice(0, 5)}-${clean.slice(5, 8)}`
    }

    const handleFetchCNPJ = async () => {
      const cleanCnpj = entDocumento.replace(/\D/g, '')
      if (cleanCnpj.length !== 14) {
        alert('Por favor, informe um CNPJ válido com 14 dígitos para buscar.')
        return
      }

      setCnpjLoading(true)
      try {
        const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`)
        if (!response.ok) {
          throw new Error('CNPJ não encontrado ou erro na API.')
        }
        const data = await response.json()
        
        if (data.razao_social) setEntNome(data.razao_social)
        if (data.nome_fantasia) setEntNomeFantasia(data.nome_fantasia)
        
        let tel = ''
        if (data.ddd_telefone_1) {
          tel = data.ddd_telefone_1
        } else if (data.telefone) {
          tel = data.telefone
        }
        if (tel) {
          setEntTelefone(formatTelefone(tel))
        }

        if (data.email) setEntEmail(data.email)
        
        let address = ''
        if (data.logradouro) address += data.logradouro
        if (data.numero) address += `, ${data.numero}`
        if (data.complemento) address += ` - ${data.complemento}`
        if (data.bairro) address += ` - ${data.bairro}`
        if (address) setEntEndereco(address)

        if (data.municipio) setEntCidade(data.municipio)
        if (data.uf) setEntUf(data.uf.toUpperCase())
        if (data.cep) setEntCep(formatCEP(data.cep))

        alert('Dados do CNPJ importados com sucesso!')
      } catch (err: any) {
        console.error(err)
        alert('Erro ao buscar CNPJ: ' + (err.message || 'CNPJ inválido ou fora do ar.'))
      } finally {
        setCnpjLoading(false)
      }
    }

    const handleCreateEntidade = (e: React.FormEvent) => {
      e.preventDefault()
      if (!entNome || !entDocumento) return

      const newEnt: Entidade = {
        id: `ent-${Date.now()}`,
        tipo_pessoa: entTipoPessoa,
        tipo_relacao: entTipoRelacao,
        nome: entNome,
        nome_fantasia: entNomeFantasia || undefined,
        documento: entDocumento,
        telefone: entTelefone,
        email: entEmail,
        endereco: entEndereco || undefined,
        cidade: entCidade || undefined,
        uf: entUf || undefined,
        cep: entCep || undefined,
        observacao: entObservacao,
        ativo: true
      }

      setEntidades(prev => [...prev, newEnt])
      setEntNome('')
      setEntNomeFantasia('')
      setEntDocumento('')
      setEntTelefone('')
      setEntEmail('')
      setEntEndereco('')
      setEntCidade('')
      setEntUf('')
      setEntCep('')
      setEntObservacao('')
      setEntSuccess(true)
      setTimeout(() => setEntSuccess(false), 3000)
    }

    const handleDeleteEntidade = (id: string) => {
      if (confirm('Tem certeza que deseja remover esta entidade?')) {
        setEntidades(prev => prev.filter(ent => ent.id !== id))
      }
    }

    const filteredEntidades = entidades.filter(ent => {
      const matchRel = entFilter === 'todos' || ent.tipo_relacao === entFilter
      const matchSearch = !entBusca.trim() || 
        ent.nome.toLowerCase().includes(entBusca.toLowerCase()) || 
        (ent.nome_fantasia && ent.nome_fantasia.toLowerCase().includes(entBusca.toLowerCase())) ||
        ent.documento.includes(entBusca)
      return matchRel && matchSearch
    })

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '20px', flex: 1, alignItems: 'start' }}>
        <div className="glass-panel" style={{ padding: '20px', height: 'fit-content' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Cadastrar Nova Entidade</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Cadastro centralizado de clientes, fornecedores, funcionários, transportadoras e representantes.
          </p>

          {entSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={16} /> Entidade cadastrada com sucesso!
            </div>
          )}

          <form onSubmit={handleCreateEntidade} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* Secção 1: Perfil */}
            <div className="form-section">
              <span className="form-section-title">1. Identificação da Entidade</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Perfil de Pessoa</label>
                  <div style={{ display: 'flex', gap: '16px', height: '40px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input type="radio" checked={entTipoPessoa === 'PJ'} onChange={() => { setEntTipoPessoa('PJ'); setEntDocumento(''); }} /> Pessoa Jurídica (PJ)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input type="radio" checked={entTipoPessoa === 'PF'} onChange={() => { setEntTipoPessoa('PF'); setEntDocumento(''); }} /> Pessoa Física (PF)
                    </label>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Relação Comercial *</label>
                  <select className="form-select" value={entTipoRelacao} onChange={e => setEntTipoRelacao(e.target.value as any)}>
                    <option value="fornecedor">Fornecedor</option>
                    <option value="cliente">Cliente</option>
                    <option value="funcionario">Funcionário</option>
                    <option value="transportadora">Transportadora</option>
                    <option value="representante">Representante</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Nome / Razão Social *</label>
                <input className="form-input" type="text" placeholder="Ex: Madeireira Pinus Sul Ltda" value={entNome} onChange={e => setEntNome(sanitizeInput(e.target.value))} required />
              </div>
              {entTipoPessoa === 'PJ' && (
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Nome Fantasia</label>
                  <input className="form-input" type="text" placeholder="Ex: Pinus Sul" value={entNomeFantasia} onChange={e => setEntNomeFantasia(sanitizeInput(e.target.value))} />
                </div>
              )}
            </div>

            {/* Secção 2: Contatos */}
            <div className="form-section">
              <span className="form-section-title">2. Documentação & Contatos</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Documento (CNPJ/CPF) *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input 
                      className="form-input" 
                      type="text" 
                      placeholder={entTipoPessoa === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'} 
                      value={entDocumento} 
                      onChange={e => setEntDocumento(formatDocumento(e.target.value, entTipoPessoa))} 
                      required 
                      style={{ flex: 1 }}
                    />
                    {entTipoPessoa === 'PJ' && (
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '0 16px', height: '40px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                        onClick={handleFetchCNPJ}
                        disabled={cnpjLoading}
                      >
                        {cnpjLoading ? <RefreshCw className="animate-spin" size={16} /> : <Search size={16} />}
                        Buscar
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Telefone</label>
                  <input className="form-input" type="text" placeholder="Ex: (41) 99999-9999" value={entTelefone} onChange={e => setEntTelefone(formatTelefone(e.target.value))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>E-mail</label>
                <input className="form-input" type="email" placeholder="Ex: comercial@empresa.com" value={entEmail} onChange={e => setEntEmail(sanitizeInput(e.target.value))} />
              </div>
            </div>

            {/* Secção 3: Endereço */}
            <div className="form-section">
              <span className="form-section-title">3. Endereço de Localidade</span>
              <div className="form-grid-3">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>CEP</label>
                  <input className="form-input" type="text" placeholder="Ex: 80000-000" value={entCep} onChange={e => setEntCep(formatCEP(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Cidade</label>
                  <input className="form-input" type="text" placeholder="Ex: Curitiba" value={entCidade} onChange={e => setEntCidade(sanitizeInput(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>UF</label>
                  <input className="form-input" type="text" maxLength={2} placeholder="Ex: PR" value={entUf} onChange={e => setEntUf(sanitizeInput(e.target.value))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Endereço Completo</label>
                <input className="form-input" type="text" placeholder="Ex: Av. das Araucárias, 2500" value={entEndereco} onChange={e => setEntEndereco(sanitizeInput(e.target.value))} />
              </div>
            </div>

            {/* Secção 4: Observações */}
            <div className="form-section">
              <span className="form-section-title">4. Observações Internas</span>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Observações</label>
                <textarea className="form-input" style={{ minHeight: '50px', resize: 'vertical' }} placeholder="Dados bancários, horário de entrega, etc..." value={entObservacao} onChange={e => setEntObservacao(sanitizeInput(e.target.value))} />
              </div>
            </div>

            <div className="form-row-actions">
              <button className="btn-primary" type="submit" style={{ padding: '12px 24px' }}>Salvar Entidade</button>
            </div>
          </form>
        </div>

        <div className="glass-panel" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '16px', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--text-high)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={20} /> Entidades Cadastradas
              </h3>
              <span style={{ fontSize: '12px', background: 'rgba(99, 102, 241, 0.15)', color: 'var(--color-primary)', padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                {filteredEntidades.length} registros
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '10px' }}>
              <div>
                <select className="form-select" value={entFilter} onChange={e => setEntFilter(e.target.value as any)}>
                  <option value="todos">Todas Relações</option>
                  <option value="fornecedor">Fornecedores</option>
                  <option value="cliente">Clientes</option>
                  <option value="funcionario">Funcionários</option>
                  <option value="transportadora">Transportadoras</option>
                  <option value="representante">Representantes</option>
                </select>
              </div>
              <div>
                <input className="form-input" type="text" placeholder="Buscar por nome, fantasia ou documento..." value={entBusca} onChange={e => setEntBusca(e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto', maxHeight: '580px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12.5px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '10px' }}>Entidade / Endereço</th>
                  <th style={{ padding: '10px' }}>Relação</th>
                  <th style={{ padding: '10px' }}>CNPJ/CPF</th>
                  <th style={{ padding: '10px' }}>Contatos</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntidades.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Nenhuma entidade cadastrada encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredEntidades.map(ent => (
                    <tr key={ent.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 10px' }}>
                        <div style={{ fontWeight: 'bold', color: 'var(--text-high)' }}>
                          {ent.nome} {ent.nome_fantasia ? `(${ent.nome_fantasia})` : ''}
                        </div>
                        {ent.endereco && (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {ent.endereco}{ent.cidade ? `, ${ent.cidade}/${ent.uf}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          background: ent.tipo_relacao === 'fornecedor' ? 'rgba(59, 130, 246, 0.15)' :
                                      ent.tipo_relacao === 'cliente' ? 'rgba(16, 185, 129, 0.15)' :
                                      ent.tipo_relacao === 'funcionario' ? 'rgba(245, 158, 11, 0.15)' :
                                      'rgba(107, 114, 128, 0.15)',
                          color: ent.tipo_relacao === 'fornecedor' ? 'var(--color-primary)' :
                                 ent.tipo_relacao === 'cliente' ? 'var(--color-success)' :
                                 ent.tipo_relacao === 'funcionario' ? 'var(--color-warning)' :
                                 'var(--text-muted)'
                        }}>
                          {ent.tipo_relacao.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px', fontFamily: 'monospace' }}>{ent.documento}</td>
                      <td style={{ padding: '12px 10px' }}>
                        <div>{ent.telefone || '-'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ent.email || ''}</div>
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                        <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '11px', color: 'var(--color-danger)' }} onClick={() => handleDeleteEntidade(ent.id)}>
                          Excluir
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  // Pre-computed sets of values in use for O(1) lookups (PERF-3)
  const paramUsageSets = useMemo(() => {
    const unidades = new Set(produtos.filter(p => p.unidade_medida_padrao).map(p => p.unidade_medida_padrao.toUpperCase()))
    const catAlmox = new Set(itensOperacionais.filter(i => i.categoria).map(i => i.categoria.toUpperCase()))
    const salas = new Set(itensOperacionais.filter(i => i.localizacao_sala).map(i => i.localizacao_sala!.toUpperCase()))
    const setores = new Set(vales.filter(v => v.setor).map(v => v.setor.toUpperCase()))
    const repComerciais = new Set([
      ...lotes.filter(l => l.cor).map(l => l.cor!.toUpperCase())
    ])
    return { unidades, catAlmox, salas, setores, repComerciais }
  }, [produtos, lotes, itensOperacionais, vales])

  const isParameterInUse = (key: keyof Omit<SystemParameters, 'grupos'>, value: string): boolean => {
    const valUpper = value.toUpperCase()
    if (key === 'unidadesMedida') return paramUsageSets.unidades.has(valUpper)
    if (key === 'categoriasAlmoxarifado') return paramUsageSets.catAlmox.has(valUpper)
    if (key === 'setoresFabrica') return paramUsageSets.setores.has(valUpper)
    if (key === 'representacoesComerciais') return paramUsageSets.repComerciais.has(valUpper)
    return false
  }

  const renderPCParametros = () => {
    const activeList = (selectedParamKey !== 'grupos_subgrupos' ? (parametros[selectedParamKey as keyof Omit<SystemParameters, 'grupos'>] || []) : []) as string[]
    
    const paramLabels: Record<keyof Omit<SystemParameters, 'grupos'>, string> = {
      unidadesMedida: 'Unidades de Medida',
      categoriasAlmoxarifado: 'Categorias de Almoxarifado',
      setoresFabrica: 'Setores da Fábrica',
      representacoesComerciais: 'Representações Comerciais'
    }

    const handleAddParam = (e: React.FormEvent) => {
      e.preventDefault()
      if (selectedParamKey === 'grupos_subgrupos') return
      if (!newParamValue.trim()) return
      const cleanVal = sanitizeInput(newParamValue)
      
      if (activeList.includes(cleanVal)) {
        alert('Este item já está cadastrado nesta lista.')
        return
      }
      
      const key = selectedParamKey as keyof Omit<SystemParameters, 'grupos'>
      setParametros(prev => ({
        ...prev,
        [key]: [...(prev[key] as string[]), cleanVal]
      }) as any)
      setNewParamValue('')
    }

    const handleDeleteParam = (val: string) => {
      if (selectedParamKey === 'grupos_subgrupos') return
      const key = selectedParamKey as keyof Omit<SystemParameters, 'grupos'>
      if (isParameterInUse(key, val)) {
        alert(`Não é possível excluir "${val}". Este parâmetro está em uso por registros ativos de produtos, lotes ou vales no sistema.`)
        return
      }

      if (confirm(`Tem certeza que deseja excluir o parâmetro "${val}"?`)) {
        setParametros(prev => ({
          ...prev,
          [key]: (prev[key] as string[]).filter(item => item !== val)
        }) as any)
      }
    }

    const handleAddGroup = (e: React.FormEvent) => {
      e.preventDefault()
      if (!newGroupName.trim()) return
      const nameClean = newGroupName.trim().toUpperCase()
      if (parametros.grupos.find(g => g.nome.toUpperCase() === nameClean)) {
        alert('Este grupo já está cadastrado.')
        return
      }
      setParametros(prev => ({
        ...prev,
        grupos: [...prev.grupos, { id: `grp-${Date.now()}`, nome: nameClean, subgrupos: [] }]
      }))
      setNewGroupName('')
    }

    const handleAddSubgroup = (e: React.FormEvent) => {
      e.preventDefault()
      if (!selectedGroupIdForSub || !newSubgroupName.trim()) {
        alert('Selecione um grupo e informe o nome do subgrupo.')
        return
      }
      const subClean = newSubgroupName.trim().toUpperCase()
      const targetGroup = parametros.grupos.find(g => g.id === selectedGroupIdForSub)
      if (!targetGroup) return

      if (targetGroup.subgrupos.some(s => s.toUpperCase() === subClean)) {
        alert('Este subgrupo já existe neste grupo.')
        return
      }

      setParametros(prev => ({
        ...prev,
        grupos: prev.grupos.map(g => g.id === selectedGroupIdForSub ? {
          ...g,
          subgrupos: [...g.subgrupos, subClean]
        } : g)
      }))
      setNewSubgroupName('')
    }

    const handleEditGroup = (g: any) => {
      const newName = prompt('Novo nome para o grupo:', g.nome)
      if (!newName || !newName.trim()) return
      const cleanNewName = newName.trim().toUpperCase()
      if (cleanNewName === g.nome) return

      if (parametros.grupos.find(x => x.nome.toUpperCase() === cleanNewName)) {
        alert('Já existe outro grupo com este nome.')
        return
      }

      setParametros(prev => ({
        ...prev,
        grupos: prev.grupos.map(x => x.id === g.id ? { ...x, nome: cleanNewName } : x)
      }))

      _setProdutos(prev => prev.map(p => p.grupo.toUpperCase() === g.nome.toUpperCase() ? {
        ...p,
        grupo: cleanNewName
      } : p))
    }

    const handleEditSubgroup = (g: any, oldSub: string) => {
      const newSub = prompt(`Novo nome para o subgrupo "${oldSub}":`, oldSub)
      if (!newSub || !newSub.trim()) return
      const cleanNewSub = newSub.trim().toUpperCase()
      if (cleanNewSub === oldSub.toUpperCase()) return

      if (g.subgrupos.some((s: string) => s.toUpperCase() === cleanNewSub)) {
        alert('Já existe este subgrupo neste grupo.')
        return
      }

      setParametros(prev => ({
        ...prev,
        grupos: prev.grupos.map(x => x.id === g.id ? {
          ...x,
          subgrupos: x.subgrupos.map(s => s.toUpperCase() === oldSub.toUpperCase() ? cleanNewSub : s)
        } : x)
      }))

      _setProdutos(prev => prev.map(p => (p.grupo.toUpperCase() === g.nome.toUpperCase() && p.subgrupo.toUpperCase() === oldSub.toUpperCase()) ? {
        ...p,
        subgrupo: cleanNewSub
      } : p))
    }

    const handleDeleteSubgroup = (g: any, sub: string) => {
      const subUpper = sub.toUpperCase()
      const grpUpper = g.nome.toUpperCase()
      const inUse = produtos.some(p => p.grupo.toUpperCase() === grpUpper && p.subgrupo.toUpperCase() === subUpper)
      
      if (inUse) {
        alert(`Não é possível excluir o subgrupo "${sub}". Ele está sendo usado por materiais cadastrados no sistema.`)
        return
      }

      if (confirm(`Tem certeza que deseja excluir o subgrupo "${sub}" do grupo "${g.nome}"?`)) {
        setParametros(prev => ({
          ...prev,
          grupos: prev.grupos.map(x => x.id === g.id ? {
            ...x,
            subgrupos: x.subgrupos.filter(s => s.toUpperCase() !== subUpper)
          } : x)
        }))
      }
    }

    const handleDeleteGroup = (g: any) => {
      const grpUpper = g.nome.toUpperCase()
      if (g.subgrupos.length > 0) {
        alert(`Não é possível excluir o grupo "${g.nome}". Exclua primeiro todos os subgrupos associados a ele.`)
        return
      }

      const inUse = produtos.some(p => p.grupo.toUpperCase() === grpUpper)
      if (inUse) {
        alert(`Não é possível excluir o grupo "${g.nome}". Existem materiais cadastrados sob este grupo.`)
        return
      }

      if (confirm(`Tem certeza que deseja excluir o grupo "${g.nome}"?`)) {
        setParametros(prev => ({
          ...prev,
          grupos: prev.grupos.filter(x => x.id !== g.id)
        }))
      }
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px', flex: 1, height: '100%' }}>
        {/* Left Side: Parameter List Selector */}
        <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-high)', fontSize: '16px' }}>Tabelas de Parâmetros</h3>
          {Object.keys(paramLabels).map((key) => {
            const isActive = selectedParamKey === key
            return (
              <button
                key={key}
                onClick={() => {
                  setSelectedParamKey(key as any)
                  setNewParamValue('')
                }}
                className="btn-secondary"
                style={{
                  justifyContent: 'flex-start',
                  fontSize: '13px',
                  padding: '10px 14px',
                  background: isActive ? 'rgba(99, 102, 241, 0.12)' : 'transparent',
                  borderColor: isActive ? 'var(--color-primary)' : 'transparent',
                  color: isActive ? 'var(--text-high)' : 'var(--text-medium)',
                  textAlign: 'left'
                }}
              >
                {paramLabels[key as keyof Omit<SystemParameters, 'grupos'>]}
              </button>
            )
          })}
          
          <button
            onClick={() => {
              setSelectedParamKey('grupos_subgrupos')
              setNewParamValue('')
            }}
            className="btn-secondary"
            style={{
              justifyContent: 'flex-start',
              fontSize: '13px',
              padding: '10px 14px',
              background: selectedParamKey === 'grupos_subgrupos' ? 'rgba(99, 102, 241, 0.12)' : 'transparent',
              borderColor: selectedParamKey === 'grupos_subgrupos' ? 'var(--color-primary)' : 'transparent',
              color: selectedParamKey === 'grupos_subgrupos' ? 'var(--text-high)' : 'var(--text-medium)',
              textAlign: 'left'
            }}
          >
            Grupos e Subgrupos
          </button>
        </div>

        {/* Right Side: Parameter Values management */}
        {selectedParamKey === 'grupos_subgrupos' ? (
          <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', textAlign: 'left' }}>
            <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-high)' }}>
                Grupos e Subgrupos de Materiais
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Gerencie os grupos e subgrupos dos materiais. A exclusão só é permitida se o item não estiver associado a nenhum material cadastrado.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Form 1: Adicionar Grupo */}
              <form onSubmit={handleAddGroup} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ margin: 0, color: 'var(--text-high)' }}>Novo Grupo</h4>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Nome do Grupo *</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Ex: FERRAGENS, MADEIRA"
                    value={newGroupName}
                    onChange={e => setNewGroupName(sanitizeInput(e.target.value))}
                    required
                  />
                </div>
                <button className="btn-primary" type="submit" style={{ height: '40px' }}>
                  Adicionar Grupo
                </button>
              </form>

              {/* Form 2: Adicionar Subgrupo */}
              <form onSubmit={handleAddSubgroup} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ margin: 0, color: 'var(--text-high)' }}>Novo Subgrupo</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Grupo Pai *</label>
                    <select className="form-select" value={selectedGroupIdForSub} onChange={e => setSelectedGroupIdForSub(e.target.value)} required>
                      <option value="">Selecione...</option>
                      {parametros.grupos.map(g => (
                        <option key={g.id} value={g.id}>{g.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Subgrupo *</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Ex: PARAFUSO, CHAPA"
                      value={newSubgroupName}
                      onChange={e => setNewSubgroupName(sanitizeInput(e.target.value))}
                      required
                    />
                  </div>
                </div>
                <button className="btn-primary" type="submit" style={{ height: '40px' }}>
                  Adicionar Subgrupo
                </button>
              </form>
            </div>

            {/* List of Groups & Subgroups */}
            <div>
              <h4 style={{ color: 'var(--text-muted)', margin: '0 0 10px 0', fontSize: '13px' }}>Grupos Cadastrados</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {parametros.grupos.map(g => {
                  const groupInUse = produtos.some(p => p.grupo.toUpperCase() === g.nome.toUpperCase())
                  return (
                    <div key={g.id} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px', color: 'var(--text-high)' }}>
                          {g.nome}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '11px', height: '28px' }} onClick={() => handleEditGroup(g)}>
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '11px', height: '28px', color: (g.subgrupos.length > 0 || groupInUse) ? 'var(--text-muted)' : 'var(--color-danger)' }}
                            onClick={() => handleDeleteGroup(g)}
                            disabled={g.subgrupos.length > 0 || groupInUse}
                          >
                            Excluir Grupo
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {g.subgrupos.length === 0 ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum subgrupo cadastrado.</span>
                        ) : (
                          g.subgrupos.map(sub => {
                            const subInUse = produtos.some(p => p.grupo.toUpperCase() === g.nome.toUpperCase() && p.subgrupo.toUpperCase() === sub.toUpperCase())
                            return (
                              <div
                                key={sub}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  background: 'var(--bg-inset)',
                                  border: '1px solid var(--border-color)',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: 'var(--text-medium)'
                                }}
                              >
                                <span>{sub}</span>
                                <button
                                  type="button"
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '10px', display: 'flex', alignItems: 'center' }}
                                  onClick={() => handleEditSubgroup(g, sub)}
                                  title="Editar"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: subInUse ? 'not-allowed' : 'pointer',
                                    color: subInUse ? 'var(--text-muted)' : 'var(--color-danger)',
                                    fontWeight: 'bold',
                                    fontSize: '10px',
                                    marginLeft: '4px'
                                  }}
                                  onClick={() => handleDeleteSubgroup(g, sub)}
                                  disabled={subInUse}
                                  title={subInUse ? "Subgrupo em uso" : "Excluir"}
                                >
                                  ✕
                                </button>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', textAlign: 'left' }}>
            <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-high)' }}>
                {paramLabels[selectedParamKey]}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                Gerencie os valores de pré-cadastro para a tabela selecionada. As alterações refletem imediatamente nos menus de autocompletar.
              </p>
            </div>

            {/* Form to add a new parameter */}
            <form onSubmit={handleAddParam} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                  Adicionar Novo Item à Tabela
                </label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Ex: NOVO PARAMETRO"
                  value={newParamValue}
                  onChange={e => setNewParamValue(sanitizeInput(e.target.value))}
                  required
                />
              </div>
              <button className="btn-primary" type="submit" style={{ padding: '10px 20px', height: '40px' }}>
                Adicionar
              </button>
            </form>

            {/* Table display */}
            <div style={{ flex: 1 }}>
              <h4 style={{ color: 'var(--text-muted)', margin: '0 0 10px 0', fontSize: '13px' }}>Itens Cadastrados</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-inset)', color: 'var(--text-muted)', fontSize: '12px' }}>
                      <th style={{ padding: '12px' }}>Parâmetro (Caixa Alta)</th>
                      <th style={{ padding: '12px' }}>Status / Uso</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeList.length === 0 ? (
                      <tr>
                        <td colSpan={3} style={{ padding: '20px', fontStyle: 'italic', textAlign: 'center', color: 'var(--text-muted)' }}>
                          Nenhum item cadastrado nesta tabela.
                        </td>
                      </tr>
                    ) : (
                      activeList.map((val) => {
                        const inUse = isParameterInUse(selectedParamKey, val)
                        const isProtected = false
                        return (
                          <tr key={val} style={{ borderBottom: '1px solid var(--border-subtle)', fontSize: '13px' }}>
                            <td style={{ padding: '12px', fontWeight: 'bold', color: 'var(--text-high)' }}>
                              {val}
                            </td>
                            <td style={{ padding: '12px' }}>
                              {isProtected ? (
                                <span className="badge" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171' }}>
                                  SISTEMA / PROTEGIDO
                                </span>
                              ) : inUse ? (
                                <span className="badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>
                                  EM USO ATIVO
                                </span>
                              ) : (
                                <span className="badge badge-success">
                                  DISPONÍVEL
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              <button
                                className="btn-secondary"
                                type="button"
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  color: (isProtected || inUse) ? 'var(--text-muted)' : 'var(--color-danger)',
                                  cursor: (isProtected || inUse) ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleDeleteParam(val)}
                                disabled={isProtected || inUse}
                              >
                                Remover
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }



  const renderPCEntradaManual = () => {
    const selectedProd = produtos.find(p => p.id === manualInboundProdId || p.nome.toUpperCase() === manualInboundProdName.toUpperCase())

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onloadend = () => {
        setManualInboundNFPhoto(reader.result as string)
      }
      reader.readAsDataURL(file)
    }

    const handleFormSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!manualInboundProdName || !manualInboundLocalCode) {
        alert('Por favor, preencha todos os campos obrigatórios.')
        return
      }

      // 1. Validar e Resolver Produto
      const prodNameClean = sanitizeInput(manualInboundProdName)
      const existingP = produtos.find(p => p.nome.toUpperCase() === prodNameClean)
      if (!existingP) {
        alert(`Erro: Material "${prodNameClean}" não cadastrado. Por favor, cadastrar o item na aba "Cadastro de Material" antes de efetuar a entrada.`)
        return
      }
      const finalProdId = existingP.id
      const prod = existingP

      // 2. Validar e Resolver Local
      const localCodeClean = sanitizeInput(manualInboundLocalCode)
      const existingL = locais.find(l => l.codigo_local.toUpperCase() === localCodeClean)
      if (!existingL) {
        alert(`Erro: Local/Endereço "${localCodeClean}" não cadastrado. Por favor, cadastre a posição de estoque na aba "Locais de Estoque" antes de efetuar a entrada.`)
        return
      }
      const finalLocalId = existingL.id

      // 3. Validar e Resolver Fornecedor
      let finalFornId = ''
      if (manualInboundFornName.trim()) {
        const fornNameClean = sanitizeInput(manualInboundFornName)
        const existingF = entidades.find(ent => ent.nome.toUpperCase() === fornNameClean && ent.tipo_relacao === 'fornecedor')
        if (!existingF) {
          alert(`Erro: Fornecedor "${fornNameClean}" não cadastrado. Por favor, cadastre o parceiro na aba "Entidades" antes de efetuar a entrada.`)
          return
        }
        finalFornId = existingF.id
      } else {
        alert('Erro: Fornecedor é obrigatório para esta operação.')
        return
      }

      const cleanLoteCode = gerarCodigoLote(
        prod.grupo,
        prod.subgrupo
      )

      // Update product's purchase and sale price (último valor atual) and control type
      _setProdutos(prev => prev.map(p => p.id === prod.id ? {
        ...p,
        preco_compra: manualInboundCusto,
        preco_venda: manualInboundVenda,
        tipo_controle: manualInboundTipoControle
      } : p))

      // Use handleCreateLote from props to register
      handleCreateLote(
        {
          codigo_lote: cleanLoteCode,
          produto_id: finalProdId,
          tom_especifico: 'Padrão Cru',
          quantidade_nota: Number(manualInboundQty),
          custo_unitario_compra: manualInboundCusto,
          fornecedor_id: finalFornId || undefined,
          numero_nota: manualInboundNFNum || undefined,
          nota_fiscal_photo: manualInboundNFPhoto || undefined,
          tipo_controle: manualInboundTipoControle,
          preco_venda_calculado: manualInboundVenda,
          data_nota_fiscal: new Date(manualInboundNFDate).toISOString()
        },
        finalLocalId
      )

      // Generate ZPL code
      const localCode = localCodeClean
      const detailsZpl = `Qtd: ${Number(manualInboundQty).toFixed(2)} ${prod.unidade_medida_padrao.toUpperCase()}`
      const nfPart = manualInboundNFNum ? `NF: ${manualInboundNFNum}` : 'NF: N/A'
      const ctrlPart = `Ctrl: ${manualInboundTipoControle.toUpperCase()}`

      const zpl = `^XA
^CF0,25
^FO40,30^FDTHOTH WMS - ENTRADA DE ESTOQUE^FS
^FO40,65^FDMaterial: ${prod.nome.substring(0, 20)}^FS
^FO40,100^FDLote: ${cleanLoteCode}^FS
^FO40,130^FD${detailsZpl} | ${ctrlPart}^FS
^FO40,160^FDLocal: ${localCode} | ${nfPart}^FS
^FO460,40^BQN,2,4^FDQA,${cleanLoteCode}^FS
^BY2,2,80
^FO40,195^BCN,80,Y,N,N^FD${cleanLoteCode}^FS
^XZ`

      setManualInboundZpl(zpl)
      setManualInboundSuccess(true)
      setShowManualZplModal(true)

      // Reset form states partially
      setManualInboundLoteCode(cleanLoteCode)
      setManualInboundQty(10)
      setManualInboundNFNum('')
      setManualInboundNFPhoto('')
      const dReset = new Date()
      const tzOffset = dReset.getTimezoneOffset() * 60000
      setManualInboundNFDate(new Date(dReset.getTime() - tzOffset).toISOString().slice(0, 16))

      setTimeout(() => setManualInboundSuccess(false), 3000)
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', flex: 1, maxWidth: '800px' }}>
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Entrada Manual de Estoque</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
            Lance a entrada individual de um lote diretamente no endereço de destino.
          </p>

          {manualInboundSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.3)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={18} /> Entrada efetuada com sucesso! Código ZPL gerado.
            </div>
          )}

          <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* Secção 1: Insumo & Parceiro */}
            <div className="form-section">
              <span className="form-section-title">1. Dados do Insumo & Fornecedor</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                    Selecionar Material *
                    <FieldTooltip text="Selecione o insumo físico recebido (deve estar pré-cadastrado)." />
                  </label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Selecione um material cadastrado" 
                    list="produtos-datalist" 
                    value={manualInboundProdName} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setManualInboundProdName(val)
                      const match = produtos.find(p => p.nome.toUpperCase() === val.toUpperCase())
                      if (match) {
                        setManualInboundProdId(match.id)
                      } else {
                        setManualInboundProdId('')
                      }
                    }} 
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                    Fornecedor *
                    <FieldTooltip text="O fabricante/parceiro cadastrado que entregou o material." />
                  </label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Selecione um fornecedor cadastrado" 
                    list="fornecedores-datalist" 
                    value={manualInboundFornName} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setManualInboundFornName(val)
                      const match = entidades.find(ent => ent.nome.toUpperCase() === val.toUpperCase() && ent.tipo_relacao === 'fornecedor')
                      if (match) {
                        setManualInboundFornId(match.id)
                      } else {
                        setManualInboundFornId('')
                      }
                    }} 
                    required
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Quantidade {selectedProd ? `(${selectedProd.unidade_medida_padrao.toUpperCase()})` : ''} *
                  </label>
                  <input 
                    className="form-input" 
                    type="number" 
                    step="any" 
                    value={manualInboundQty} 
                    onChange={e => setManualInboundQty(Number(e.target.value))}
                    required 
                  />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', paddingBottom: '8px' }}>
                    Sem especificações adicionais de cor.
                  </div>
                </div>
              </div>
            </div>

            {/* Secção 2: Localização e Controle */}
            <div className="form-section">
              <span className="form-section-title">2. Endereço & Tipo de Controle</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                    Endereço Destino *
                    <FieldTooltip text="O endereço físico do WMS onde o material será depositado." />
                  </label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Ex: GAIOLA-T1" 
                    list="locais-datalist" 
                    value={manualInboundLocalCode} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setManualInboundLocalCode(val)
                      const match = locais.find(l => l.codigo_local.toUpperCase() === val.toUpperCase())
                      if (match) {
                        setManualInboundLocalId(match.id)
                      } else {
                        setManualInboundLocalId('')
                      }
                    }} 
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Tipo de Controle *
                  </label>
                  <select 
                    className="form-select" 
                    value={manualInboundTipoControle} 
                    onChange={e => setManualInboundTipoControle(e.target.value as any)}
                    required
                  >
                    <option value="simples">SIMPLES (SALDO GERAL)</option>
                    <option value="serializado">SERIALIZADO (PEÇA A PEÇA)</option>
                    <option value="patrimonio">PATRIMÔNIO (OPERACIONAL)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Secção 3: Valores Financeiros */}
            <div className="form-section">
              <span className="form-section-title">3. Valores Comerciais</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Preço de Compra (Custo Lote R$)</label>
                  <input className="form-input" type="text" value={formatCurrency(manualInboundCusto)} onChange={e => setManualInboundCusto(parseCurrencyInput(e.target.value))} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Preço de Venda (+30% Sugerido R$)</label>
                  <input className="form-input" type="text" value={formatCurrency(manualInboundVenda)} onChange={e => setManualInboundVenda(parseCurrencyInput(e.target.value))} />
                </div>
              </div>
            </div>

            {/* Secção 4: Documento Fiscal */}
            <div className="form-section">
              <span className="form-section-title">4. Documento de Auditoria (Nota Fiscal)</span>
              <div className="form-grid-3" style={{ alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Número da Nota Fiscal
                  </label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Ex: NF-004312" 
                    value={manualInboundNFNum} 
                    onChange={e => setManualInboundNFNum(sanitizeInput(e.target.value))} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Data/Hora da Nota Fiscal
                  </label>
                  <input 
                    className="form-input" 
                    type="datetime-local" 
                    value={manualInboundNFDate} 
                    onChange={e => setManualInboundNFDate(e.target.value)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Foto da Nota Fiscal
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handlePhotoChange} 
                      style={{ fontSize: '12px', color: 'var(--text-medium)', width: '100%' }} 
                    />
                    {manualInboundNFPhoto && (
                      <div style={{ position: 'relative', width: '38px', height: '38px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border-color)', flexShrink: 0 }}>
                        <img src={manualInboundNFPhoto} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button 
                          type="button" 
                          onClick={() => setManualInboundNFPhoto('')} 
                          style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(239,68,68,0.85)', color: '#fff', border: 'none', borderRadius: '50%', width: '14px', height: '14px', fontSize: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row-actions">
              <button className="btn-primary" type="submit" style={{ padding: '12px 24px' }}>
                Registrar Entrada
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  const renderPCSaida = () => {
    const handleFormSaidaSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      
      const prod = produtos.find(p => p.nome.toUpperCase() === manualOutboundProdName.toUpperCase())
      if (!prod) {
        alert('Material não cadastrado ou não selecionado corretamente.')
        return
      }

      if (!manualOutboundLoteId) {
        alert('Selecione um lote/local de origem para a retirada do material.')
        return
      }

      const selectedSaldo = saldos.find(s => `${s.produto_id}_${s.lote_id}` === manualOutboundLoteId)
      if (!selectedSaldo) {
        alert('Saldo não encontrado para o lote/local selecionado.')
        return
      }

      if (manualOutboundQty <= 0) {
        alert('A quantidade de saída deve ser maior que zero.')
        return
      }

      if (manualOutboundQty > selectedSaldo.quantidade_fisica + 0.00001) {
        alert(`Quantidade insuficiente! Saldo disponível neste lote/local: ${selectedSaldo.quantidade_fisica} ${prod.unidade_medida_padrao.toUpperCase()}`)
        return
      }

      // Add Movimentacao record (derived state will update saldos automatically!)
      handleAddMovimentacao({
        produto_id: prod.id,
        lote_id: selectedSaldo.lote_id,
        tipo_movimento: 'saida',
        quantidade: manualOutboundQty,
        local_origem_id: selectedSaldo.local_id,
        local_destino_id: '',
        usuario: usuarioLogado?.username || 'admin',
        motivo: manualOutboundMotivo
      })

      setManualOutboundSuccess(true)
      // Reset form
      setManualOutboundProdName('')
      setManualOutboundLoteId('')
      setManualOutboundQty(0)
      setManualOutboundSetor('')

      setTimeout(() => setManualOutboundSuccess(false), 3000)
    }

    // Filter stock locations/lotes for the selected product
    const prodMatch = produtos.find(p => p.nome.toUpperCase() === manualOutboundProdName.toUpperCase())
    const availableStock = prodMatch 
      ? saldos.filter(s => s.produto_id === prodMatch.id && s.quantidade_fisica > 0)
      : []

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', flex: 1, maxWidth: '800px' }}>
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Saída de Estoque (Retirada)</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
            Lance a saída ou descarte direto de materiais e insumos do estoque.
          </p>

          {manualOutboundSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.3)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={18} /> Saída registrada com sucesso! Estoque atualizado.
            </div>
          )}

          <form onSubmit={handleFormSaidaSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* Secção 1: Origem */}
            <div className="form-section">
              <span className="form-section-title">1. Origem da Carga (Estoque Físico)</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Selecionar Material *
                  </label>
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder="Selecione um material cadastrado" 
                    list="produtos-datalist" 
                    value={manualOutboundProdName} 
                    onChange={e => {
                      const val = sanitizeInput(e.target.value)
                      setManualOutboundProdName(val)
                      setManualOutboundLoteId('')
                    }} 
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Lote / Endereço de Origem *
                  </label>
                  <select 
                    className="form-select" 
                    value={manualOutboundLoteId} 
                    onChange={e => setManualOutboundLoteId(e.target.value)} 
                    required
                    disabled={!manualOutboundProdName || availableStock.length === 0}
                  >
                    <option value="">
                      {!manualOutboundProdName 
                        ? '-- Selecione primeiro o material --' 
                        : availableStock.length === 0 
                          ? '-- Sem estoque disponível --' 
                          : '-- Selecione o Lote/Local --'}
                    </option>
                    {availableStock.map(s => {
                      const l = lotes.find(lot => lot.id === s.lote_id)
                      const loc = locais.find(local => local.id === s.local_id)
                      return (
                        <option key={`${s.produto_id}_${s.lote_id}`} value={`${s.produto_id}_${s.lote_id}`}>
                          Lote: {l?.codigo_lote || 'N/A'} ({l?.tom_especifico || 'Padrão Cru'}) - Local: {loc?.codigo_local || 'N/A'} (Saldo: {s.quantidade_fisica} {prodMatch?.unidade_medida_padrao.toUpperCase()})
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>
            </div>

            {/* Secção 2: Baixa */}
            <div className="form-section">
              <span className="form-section-title">2. Destinatário & Quantidade de Retirada</span>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Quantidade de Retirada *
                  </label>
                  <input 
                    className="form-input" 
                    type="number" 
                    step="any" 
                    value={manualOutboundQty || ''} 
                    onChange={e => setManualOutboundQty(Number(e.target.value))}
                    required 
                    min={0.0001}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                    Motivo da Saída *
                  </label>
                  <select 
                    className="form-select" 
                    value={manualOutboundMotivo} 
                    onChange={e => {
                      setManualOutboundMotivo(e.target.value as any)
                      setManualOutboundSetor('')
                    }} 
                    required
                  >
                    <option value="producao">Consumo Fábrica (Produção)</option>
                    <option value="perda">Avaria / Perda / Descarte</option>
                    <option value="venda">Venda Direta / Faturamento</option>
                    <option value="emprestimo">Empréstimo Temporário</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>
                  {manualOutboundMotivo === 'producao' ? 'Setor da Fábrica (Destino) *' : 'Parceiro / Cliente / Destino *'}
                </label>
                {manualOutboundMotivo === 'producao' ? (
                  <select 
                    className="form-select" 
                    value={manualOutboundSetor} 
                    onChange={e => setManualOutboundSetor(e.target.value)} 
                    required
                  >
                    <option value="">-- Selecione o Setor --</option>
                    {parametros.setoresFabrica.map(s => (
                      <option key={s} value={s}>{s.toUpperCase()}</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    className="form-input" 
                    type="text" 
                    placeholder={manualOutboundMotivo === 'perda' ? 'Ex: Descarte por Avaria de Molhadura' : 'Selecione ou digite o parceiro/cliente'} 
                    list={manualOutboundMotivo === 'venda' ? 'clientes-datalist' : 'entidades-datalist'} 
                    value={manualOutboundSetor} 
                    onChange={e => setManualOutboundSetor(sanitizeInput(e.target.value))}
                    required
                  />
                )}
              </div>
            </div>

            <div className="form-row-actions">
              <button className="btn-primary" type="submit" style={{ padding: '12px 24px', background: 'linear-gradient(135deg, var(--color-danger), var(--color-secondary))' }}>
                Confirmar Saída
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }



  // UI for User Management Tab
  const renderPCUsuarios = () => {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '24px', flex: 1, alignItems: 'start' }}>
        {/* Form to create new user */}
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)' }}>Novo Operador / Usuário</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
            Adicione um novo colaborador ao sistema e atribua seu nível de acesso.
          </p>

          {usrSuccess && (
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.3)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={18} /> Usuário criado com sucesso no banco!
            </div>
          )}

          <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Nome Completo</label>
              <input
                className="form-input"
                type="text"
                placeholder="Ex: João Silva"
                value={newUsrFullName}
                onChange={e => setNewUsrFullName(sanitizeInput(e.target.value))}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Nome de Usuário (Username)</label>
              <input
                className="form-input"
                type="text"
                placeholder="Ex: JOAO.SILVA"
                value={newUsrUsername}
                onChange={e => setNewUsrUsername(sanitizeInput(e.target.value))}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Senha de Acesso</label>
              <input
                className="form-input"
                type="password"
                placeholder="Mínimo 3 caracteres"
                value={newUsrPassword}
                onChange={e => setNewUsrPassword(sanitizeInput(e.target.value))}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Cargo / Nível de Acesso</label>
              <select
                className="form-select"
                value={newUsrRole}
                onChange={e => setNewUsrRole(e.target.value as any)}
                required
              >
                <option value="operador">Operador (Acesso Coletor / Balanço)</option>
                <option value="comprador">Comprador (Acesso Compras / Reservas)</option>
                <option value="diretoria">Diretoria (Apenas Leitura / Dashboard)</option>
                <option value="admin">Administrador (Acesso Total)</option>
              </select>
            </div>

            <button type="submit" className="btn-primary" style={{ justifyContent: 'center' }}>
              Cadastrar Usuário
            </button>
          </form>
        </div>

        {/* List of registered users */}
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-high)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={20} /> Usuários Cadastrados
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
            Gerencie os operadores e cargos ativos no sistema.
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '10px' }}>Nome</th>
                <th style={{ padding: '10px' }}>Username</th>
                <th style={{ padding: '10px' }}>Nível</th>
                <th style={{ padding: '10px', textAlign: 'center', width: '80px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.username} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.03)' }}>
                  <td style={{ padding: '12px 10px', color: 'var(--text-high)', fontWeight: '500' }}>{u.nome}</td>
                  <td style={{ padding: '12px 10px' }}>
                    <code style={{ fontSize: '12px', background: 'var(--bg-inset)', padding: '2px 6px', borderRadius: '4px' }}>{u.username}</code>
                  </td>
                  <td style={{ padding: '12px 10px' }}>
                    <span className={`badge ${u.nivel === 'admin' ? 'badge-danger' : u.nivel === 'operador' ? 'badge-primary' : 'badge-success'}`}>
                      {u.nivel}
                    </span>
                  </td>
                  <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                    {u.username.toUpperCase() !== 'ADMIN' && (
                      <button
                        className="btn-secondary"
                        style={{ padding: '6px 8px', color: 'var(--color-danger)', borderColor: 'rgba(244,63,94,0.15)' }}
                        onClick={() => handleDeleteUser(u.username)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Router for tabs
  const renderPCContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderPCDashboard()
      // Almoxarifado
      case 'almox_cadastro': return renderPCCadastroMaterial()
      case 'almox_entrada': return renderPCEntradaManual()
      case 'almox_saida': return renderPCSaida()
      case 'almox_putaway': return renderPCPutaway()
      case 'almox_locais': return renderPCLocais()
      // Operacional
      case 'operacional_vales': return renderPCFerramentas()
      // Comercial
      case 'comercial_reservas': return renderPCReservas()
      case 'comercial_compras': return renderPCCompras()
      // Cadastros
      case 'cadastros_entidades': return renderPCEntidades()
      // Utilitários
      case 'utilitarios_calculadora': return renderPCCalculadora()
      case 'utilitarios_balanco': return renderPCBalanco()
      // Admin
      case 'admin_parametros': return renderPCParametros()
      case 'admin_usuarios': return renderPCUsuarios()
      default: return renderPCDashboard()
    }
  }

  const renderPCSidebar = () => {
    // Define collapsible navigation structure
    const groups = [
      {
        id: 'painel',
        label: 'Painel Geral',
        isGroup: false,
        linkId: 'dashboard',
        icon: LayoutDashboard
      },
      {
        id: 'almoxarifado',
        label: 'Almoxarifado',
        isGroup: true,
        icon: Warehouse,
        subLinks: [
          { id: 'almox_cadastro', label: 'Cadastro de Materiais', icon: ClipboardPlus, permissionKey: 'cadastro' },
          { id: 'almox_entrada', label: 'Entrada de Estoque', icon: ArrowDownLeft, permissionKey: 'importacao' },
          { id: 'almox_saida', label: 'Saída de Estoque', icon: ArrowUpRight, permissionKey: 'saida' },
          { id: 'almox_putaway', label: 'Guarda & Putaway', icon: Archive, permissionKey: 'putaway' },
          { id: 'almox_locais', label: 'Locais de Estoque', icon: MapPin, permissionKey: 'locais' }
        ]
      },
      {
        id: 'operacional',
        label: 'Operacional',
        isGroup: true,
        icon: Wrench,
        subLinks: [
          { id: 'operacional_vales', label: 'Controle de Vales', icon: FileSignature, permissionKey: 'ferramentas' }
        ]
      },
      {
        id: 'comercial',
        label: 'Comercial',
        isGroup: true,
        icon: DollarSign,
        subLinks: [
          { id: 'comercial_reservas', label: 'Reservas & Orçamentos', icon: FileText, permissionKey: 'reservas' },
          { id: 'comercial_compras', label: 'Sugestão de Compras', icon: ShoppingCart, permissionKey: 'compras' }
        ]
      },
      {
        id: 'cadastros',
        label: 'Cadastros',
        isGroup: true,
        icon: Users,
        subLinks: [
          { id: 'cadastros_entidades', label: 'Entidades', icon: Users, permissionKey: 'entidades' }
        ]
      },
      {
        id: 'utilitarios',
        label: 'Utilitários',
        isGroup: true,
        icon: Scale,
        subLinks: [
          { id: 'utilitarios_calculadora', label: 'Calculadoras', icon: Calculator, permissionKey: 'calculadora' },
          { id: 'utilitarios_balanco', label: 'Ajuste (Balanço)', icon: Scale, permissionKey: 'balanco' }
        ]
      },
      {
        id: 'admin',
        label: 'Configuração',
        isGroup: true,
        icon: Settings,
        subLinks: [
          { id: 'admin_parametros', label: 'Parâmetros do Sistema', icon: Sliders, permissionKey: 'parametros' },
          { id: 'admin_usuarios', label: 'Controle de Usuários', icon: Users, permissionKey: 'usuarios' }
        ]
      }
    ]

    return (
      <div className="glass-panel" style={{ width: '270px', padding: '20px 10px', display: 'flex', flexDirection: 'column', gap: '4px', height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        <div style={{ padding: '10px 15px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '22px', background: 'linear-gradient(135deg, #a855f7, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>THOTH WMS</h2>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>MÓDULO ADMINISTRATIVO</span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          {groups.map(g => {
            if (!usuarioLogado) return null

            if (!g.isGroup) {
              // Direct Link (e.g. Dashboard)
              const linkId = g.linkId!
              const hasPermission = permissoes[usuarioLogado.nivel]?.[linkId] !== false
              if (!hasPermission) return null

              const Icon = g.icon
              const isActive = activeTab === linkId
              return (
                <button
                  key={linkId}
                  onClick={() => setActiveTab(linkId)}
                  className="btn-secondary"
                  style={{
                    justifyContent: 'flex-start',
                    background: isActive ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                    borderColor: isActive ? 'var(--color-primary)' : 'transparent',
                    color: isActive ? 'var(--text-high)' : 'var(--text-medium)',
                    width: '100%'
                  }}
                >
                  <Icon size={18} />
                  {g.label}
                </button>
              )
            } else {
              // Collapsible Group
              const subLinksFiltrados = g.subLinks!.filter(l => {
                const key = l.permissionKey || l.id
                return permissoes[usuarioLogado.nivel]?.[key] !== false
              })
              if (subLinksFiltrados.length === 0) return null

              const isExpanded = !!expandedGroups[g.id]
              const GroupIcon = g.icon

              return (
                <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {/* Group Header Button */}
                  <button
                    onClick={() => toggleGroup(g.id)}
                    className="btn-secondary"
                    style={{
                      justifyContent: 'space-between',
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid transparent',
                      borderColor: 'rgba(255, 255, 255, 0.05)',
                      color: isExpanded ? 'var(--text-high)' : 'var(--text-muted)',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      padding: '8px 12px',
                      width: '100%'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <GroupIcon size={16} style={{ opacity: 0.7 }} />
                      <span>{g.label}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>

                  {/* Group Children Links */}
                  {isExpanded && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '12px', borderLeft: '1px solid var(--border-color)', marginLeft: '12px', marginTop: '2px', marginBottom: '6px' }}>
                      {subLinksFiltrados.map(l => {
                        const Icon = l.icon
                        const isActive = activeTab === l.id
                        return (
                          <button
                            key={l.id}
                            onClick={() => setActiveTab(l.id)}
                            className="btn-secondary"
                            style={{
                              justifyContent: 'flex-start',
                              background: isActive ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                              borderColor: isActive ? 'var(--color-primary)' : 'transparent',
                              color: isActive ? 'var(--text-high)' : 'var(--text-medium)',
                              width: '100%',
                              padding: '6px 12px',
                              fontSize: '13px'
                            }}
                          >
                            <Icon size={16} />
                            {l.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }
          })}
        </div>

        {/* Separator and link to Mobile version */}
        <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 'auto', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <a
            href="#/mobile"
            className="btn-secondary"
            style={{
              justifyContent: 'flex-start',
              textDecoration: 'none',
              width: '100%',
              background: 'rgba(168, 85, 247, 0.05)',
              borderColor: 'rgba(168, 85, 247, 0.2)',
              color: 'var(--color-secondary)'
            }}
          >
            <Smartphone size={18} />
            Coletor PWA Mobile
          </a>
        </div>
      </div>
    )
  }

  // 13. Login Form Rendering
  if (!usuarioLogado) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-main)',
        backgroundImage: 'radial-gradient(circle at top right, rgba(99, 102, 241, 0.05), transparent 40%), radial-gradient(circle at bottom left, rgba(168, 85, 247, 0.05), transparent 40%)'
      }}>
        <div className="glass-panel" style={{
          padding: '40px',
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '26px', background: 'linear-gradient(135deg, #a855f7, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0, fontWeight: 'bold' }}>
              WMS THOTH
            </h2>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Módulo Administrativo - Login</span>
          </div>

          {loginErr && (
            <div style={{
              background: 'rgba(244, 63, 94, 0.12)',
              border: '1px solid rgba(244, 63, 94, 0.2)',
              color: 'var(--color-danger)',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '13px',
              textAlign: 'center'
            }}>
              {loginErr}
            </div>
          )}

          <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Usuário</label>
              <input
                className="form-input"
                type="text"
                placeholder="Digite seu usuário"
                value={loginUser}
                onChange={e => setLoginUser(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '13px', color: 'var(--text-high)', display: 'block', marginBottom: '6px' }}>Senha</label>
              <input
                className="form-input"
                type="password"
                placeholder="Digite sua senha"
                value={loginPass}
                onChange={e => setLoginPass(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn-primary" style={{ justifyContent: 'center', width: '100%', padding: '12px' }}>
              Entrar no Painel
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <header style={{ background: 'var(--header-bg)', borderBottom: '1px solid var(--border-color)', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <QrCode size={24} style={{ color: 'var(--color-primary)' }} />
          <h1 style={{ fontSize: '20px', margin: 0, color: 'var(--text-high)', letterSpacing: 'normal' }}>
            WMS THOTH <span style={{ fontSize: '13px', fontWeight: 'normal', color: 'var(--text-muted)' }}>— PAINEL ADMINISTRATIVO</span>
          </h1>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {usuarioLogado && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderRight: '1px solid var(--border-color)', paddingRight: '12px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-high)' }}>{usuarioLogado.nome}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{usuarioLogado.nivel}</div>
              </div>
              <button
                className="btn-secondary"
                style={{ padding: '6px 12px', fontSize: '12px', color: 'var(--color-danger)', borderColor: 'rgba(244, 63, 94, 0.2)' }}
                onClick={() => {
                  setUsuarioLogado(null)
                  setActiveTab('dashboard')
                }}
              >
                Sair
              </button>
            </div>
          )}
          <button
            className="btn-secondary"
            onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
            style={{
              padding: '10px',
              justifyContent: 'center',
              width: '42px',
              height: '42px',
              borderRadius: '10px'
            }}
            title={theme === 'light' ? 'Alternar para Modo Escuro' : 'Alternar para Modo Claro'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', gap: '20px', padding: '20px', flex: 1 }}>
        {renderPCSidebar()}
        <main style={{ flex: 1, overflowY: 'auto', display: 'flex' }}>
          {renderPCContent()}
        </main>
      </div>

      {showValePrintModal && selectedValeForPrint && (() => {
        const item = itensOperacionais.find(i => i.id === selectedValeForPrint.item_operacional_id)
        const zpl = `^XA
^CF0,25
^FO40,40^FDWMS THOTH - Prince Box^FS
^FO40,80^FDTipo: ${selectedValeForPrint.tipo.toUpperCase()}^FS
^FO40,110^FDOpe: ${selectedValeForPrint.funcionario_nome}^FS
^FO40,140^FDCPF: ${selectedValeForPrint.cpf_cnpj}^FS
^FO40,170^FDSetor: ${selectedValeForPrint.setor}^FS
^FO40,200^FDItem: ${item?.nome || 'Outros'}^FS
^FO40,230^FDStatus: ${selectedValeForPrint.status.toUpperCase()}^FS
^FO40,260^FDValor: R$ ${selectedValeForPrint.valor_cobranca.toFixed(2)}^FS
^FO40,290^FDAssinatura: ___________________^FS
^XZ
`

        return (
          <div className="modal-backdrop" style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
            <div className="glass-panel" style={{ width: '420px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                <h4 style={{ margin: 0, color: 'var(--text-high)' }}>Comprovante Térmico & ZPL</h4>
                <button className="btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => {
                  setShowValePrintModal(false)
                  setSelectedValeForPrint(null)
                }}>Fechar</button>
              </div>

              <div className="thermal-print-area" id="print-vale-ticket" style={{ background: '#fff', color: '#000', padding: '16px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', border: '1px dashed #ccc', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '10px', marginBottom: '5px' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 'bold' }}>WMS THOTH</h3>
                  <div style={{ fontSize: '10px' }}>PRINCE CAMA BOX</div>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '6px' }}>
                    VIA DE COMPROVANTE - ALMOXARIFADO
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div><strong>ID Vale:</strong> {selectedValeForPrint.id}</div>
                  <div><strong>Operação:</strong> {selectedValeForPrint.tipo.toUpperCase()}</div>
                  <div><strong>Data:</strong> {new Date(selectedValeForPrint.data_emprestimo).toLocaleString('pt-BR')}</div>
                  <div><strong>Colaborador/Cli:</strong> {selectedValeForPrint.funcionario_nome}</div>
                  <div><strong>CPF/CNPJ:</strong> {selectedValeForPrint.cpf_cnpj}</div>
                  <div><strong>Setor:</strong> {selectedValeForPrint.setor}</div>
                  <div style={{ borderTop: '1px dashed #000', paddingTop: '6px', marginTop: '4px' }}>
                    <strong>Item:</strong> {item?.nome}
                  </div>
                  {item?.codigo_patrimonio && <div><strong>Patrimônio:</strong> {item.codigo_patrimonio}</div>}
                  <div><strong>Valor Reposição/Venda:</strong> R$ {selectedValeForPrint.valor_cobranca.toFixed(2)}</div>
                  <div><strong>Status:</strong> {selectedValeForPrint.status.toUpperCase()}</div>
                </div>

                <div style={{ borderTop: '1px dashed #000', paddingTop: '15px', marginTop: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', marginBottom: '20px' }}>DECLARO QUE RECEBI O ITEM ACIMA EM PERFEITAS CONDIÇÕES.</div>
                  <div style={{ borderBottom: '1px solid #000', width: '80%', margin: '0 auto 5px auto' }}></div>
                  <div style={{ fontSize: '10px', marginBottom: '10px' }}>Assinatura do Recebedor</div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px', gap: '4px' }}>
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${selectedValeForPrint.id}`} alt="QR Code Vale" style={{ width: '100px', height: '100px' }} />
                    <span style={{ fontSize: '10px', fontWeight: 'bold' }}>{selectedValeForPrint.id}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold' }}>Código ZPL (Para Impressora Zebra):</span>
                <textarea 
                  className="form-input" 
                  readOnly 
                  value={zpl} 
                  style={{ fontSize: '11px', minHeight: '120px', fontFamily: 'monospace', background: 'var(--bg-inset)', resize: 'none' }} 
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => {
                  const printContents = document.getElementById('print-vale-ticket')?.innerHTML;
                  if (printContents) {
                    const w = window.open();
                    if (w) {
                      w.document.write(`
                        <html>
                          <head>
                            <title>Comprovante Vale Almoxarifado</title>
                            <style>
                              body { font-family: monospace; font-size: 12px; margin: 0; padding: 10px; width: 80mm; }
                              strong { font-weight: bold; }
                              div { margin-bottom: 4px; }
                            </style>
                          </head>
                          <body onload="window.print(); window.close();">
                            ${printContents}
                          </body>
                        </html>
                      `);
                      w.document.close();
                    }
                  }
                }}>
                  Imprimir Cupom (Térmica)
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {showManualZplModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="glass-panel" style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '30px',
            width: '100%',
            maxWidth: '550px',
            boxShadow: 'var(--shadow-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-high)' }}>Etiqueta Zebra ZPL Geradas</h3>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>VIA DE RECEBIMENTO MANUAL</span>
              </div>
              <button className="btn-secondary" onClick={() => setShowManualZplModal(false)}>Fechar</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold' }}>VISUALIZAÇÃO DA ETIQUETA:</span>
              <div style={{
                background: '#fff',
                color: '#000',
                padding: '16px',
                borderRadius: '8px',
                border: '2px solid #333',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: '145px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '70%', fontSize: '11px', textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '4px' }}>THOTH WMS - ENTRADA MANUAL</div>
                  <div><strong>Material:</strong> {manualInboundProdName}</div>
                  <div><strong>Lote:</strong> {manualInboundLoteCode}</div>
                  <div><strong>Qtd:</strong> {manualInboundQty}</div>
                  <div><strong>Local:</strong> {manualInboundLocalCode}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${manualInboundLoteCode}`} alt="QR Code Lote" style={{ width: '80px', height: '80px' }} />
                  <span style={{ fontSize: '8px', fontWeight: 'bold' }}>{manualInboundLoteCode}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold' }}>CÓDIGO DE ETIQUETA (ZPL):</span>
              <pre className="zpl-preview" style={{
                fontSize: '11px',
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'var(--bg-inset)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                fontFamily: 'Courier New, monospace',
                color: 'var(--text-high)',
                whiteSpace: 'pre-wrap'
              }}>{manualInboundZpl || 'Nenhuma etiqueta gerada'}</pre>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-secondary" onClick={() => setShowManualZplModal(false)}>Fechar</button>
              <button className="btn-primary" onClick={() => {
                alert('Etiqueta enviada com sucesso para a impressora Zebra industrial!')
                setShowManualZplModal(false)
              }}>
                <Printer size={16} /> Mock Enviar para Impressora
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'none' }}>
        <datalist id="produtos-datalist">
          {produtos.map(p => <option key={p.id} value={p.nome} />)}
        </datalist>
        <datalist id="locais-datalist">
          {locais.map(l => <option key={l.id} value={l.codigo_local} />)}
        </datalist>
        <datalist id="fornecedores-datalist">
          {entidades.filter(e => e.tipo_relacao === 'fornecedor').map(e => <option key={e.id} value={e.nome} />)}
        </datalist>
        <datalist id="clientes-datalist">
          {entidades.filter(e => e.tipo_relacao === 'cliente').map(e => <option key={e.id} value={e.nome} />)}
        </datalist>
        <datalist id="entidades-datalist">
          {entidades.map(e => <option key={e.id} value={e.nome} />)}
        </datalist>
        <datalist id="categorias-datalist">
          {uniqueGrupos.map(c => <option key={c} value={c} />)}
        </datalist>
        <datalist id="tons-datalist">
          {uniqueTons.map(t => <option key={t} value={t} />)}
        </datalist>
        <datalist id="cores-datalist">
          {uniqueCores.map(c => <option key={c} value={c} />)}
        </datalist>
        <datalist id="marcas-datalist">
          {uniqueMarcas.map(m => <option key={m} value={m} />)}
        </datalist>

        <datalist id="salas-datalist">
          {uniqueSalas.map(s => <option key={s} value={s} />)}
        </datalist>
        <datalist id="funcionarios-datalist">
          {registeredFuncionarios.map(f => <option key={f.id} value={f.nome} />)}
        </datalist>
        <datalist id="setores-datalist">
          {uniqueSetores.map(s => <option key={s} value={s} />)}
        </datalist>
        <datalist id="itens-operacionais-datalist">
          {itensOperacionais.map(item => <option key={item.id} value={item.nome} />)}
        </datalist>
        <datalist id="unidades-medida-datalist">
          {parametros.unidadesMedida.map(u => <option key={u} value={u} />)}
        </datalist>
        <datalist id="madeiras-datalist">
          {(parametros.grupos.find(g => g.nome === 'MADEIRA')?.subgrupos || []).map(m => <option key={m} value={m} />)}
        </datalist>
        <datalist id="agrupadores-datalist">
          {Array.from(new Set(produtos.map(p => p.agrupador_comercial))).map(a => <option key={a} value={a} />)}
        </datalist>
      </div>
    </div>
  )
}
